import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { format, parseISO } from 'date-fns';

import { HeaderComponent } from '../common/header/header.component';
import { SidebarComponent } from '../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../common/paginator/paginator.component';
import { OverlayLoaderComponent } from '../common/loader/overlay-loader/overlay-loader.component';

import { SearchHistoryService } from '../../services/search-history/search-history.service';

@Component({
  selector: 'app-search-history',
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    PaginatorComponent,
    SidebarComponent,
    OverlayLoaderComponent,
  ],
  templateUrl: './search-history.component.html',
  styleUrl: './search-history.component.css',
})
export class SearchHistoryComponent implements OnInit, OnDestroy {
  isLoading = false;
  showDeleteConfirmation = false;
  pendingDeleteAction: string = '';
  subscriptionDetails: any = {};

  // Debounce Subjects for search inputs
  private imageHistorySearchNameSubject = new Subject<string>();
  private faceCompareHistoryFileNameSubject = new Subject<string>();
  private faceCompareHistoryNotesSubject = new Subject<string>();
  private videoScannerHistoryVideoNameSubject = new Subject<string>();
  private osintHistoryPhoneSubject = new Subject<string>();
  private osintHistoryEmailSubject = new Subject<string>();
  private osintHistoryUsernameSubject = new Subject<string>();
  private osintHistoryNameSubject = new Subject<string>();
  private osintHistoryWalletSubject = new Subject<string>();
  private osintHistoryGstSubject = new Subject<string>();
  private osintHistoryDinSubject = new Subject<string>();
  private leakHistoryPhoneSubject = new Subject<string>();
  private leakHistoryEmailSubject = new Subject<string>();
  private leakHistoryUsernameSubject = new Subject<string>();
  private leakHistoryPasswordSubject = new Subject<string>();
  private vehicleHistoryIdSubject = new Subject<string>();
  private vehicleHistoryVehicleNumberSubject = new Subject<string>();
  private panHistoryIdSubject = new Subject<string>();
  private panHistoryPanNumberSubject = new Subject<string>();
  private virtualNumberHistoryIdSubject = new Subject<string>();
  private virtualNumberHistoryVirtualNumberSubject = new Subject<string>();
  private advancedSearchHistoryIdSubject = new Subject<string>();
  private advancedSearchHistorySearchNameSubject = new Subject<string>();
  private basicSearchHistoryPhoneSubject = new Subject<string>();
  private webSearchHistoryUrlSubject = new Subject<string>();
  private webSearchHistoryDomainSubject = new Subject<string>();
  private linkAnalysisRootValueSubject = new Subject<string>();
  private linkAnalysisRootTypeSubject = new Subject<string>();
  private linkAnalysisStatusSubject = new Subject<string>();

  private subscriptions: Subscription[] = [];

  /**
   * Used by the UI to decide whether to show "Delete" vs "Delete all"
   * when multiple entries are selected.
   */
  get selectedEntriesCount(): number {
    const t = this.selectedHistoryTab?.type;

    if (t === 'image') return this.selectedImageHistories?.length || 0;
    if (t === 'face-compare')
      return this.selectedFaceCompareHistories?.length || 0;
    if (t === 'video-scanner')
      return this.selectedVideoScannerHistories?.length || 0;

    if (
      t === 'osint-phone' ||
      t === 'osint-email' ||
      t === 'osint-username' ||
      t === 'osint-name' ||
      t === 'osint-wallet'
    ) {
      return this.selectedOsintHistories?.length || 0;
    }

    if (
      t === 'leak-phone' ||
      t === 'leak-email' ||
      t === 'leak-username' ||
      t === 'leak-password'
    ) {
      return this.selectedLeakHistories?.length || 0;
    }

    if (t === 'vehicle') return this.selectedVehicleHistories?.length || 0;
    if (t === 'pan') return this.selectedPanHistories?.length || 0;
    if (t === 'gst') return this.selectedGstHistories?.length || 0;
    if (t === 'din') return this.selectedDinHistories?.length || 0;
    if (t === 'mobile-pan') return this.selectedMobilePanHistories?.length || 0;
    if (t === 'phonepe_fastag')
      return this.selectedPhonepeFastagHistories?.length || 0;
    if (t === 'phonepe_upi')
      return this.selectedPhonepeUpiHistories?.length || 0;
    if (t === 'virtual-number')
      return this.selectedVirtualNumberHistories?.length || 0;
    if (t === 'advanced-search')
      return this.selectedAdvancedSearchHistories?.length || 0;
    if (t === 'basic-search')
      return this.selectedBasicSearchHistories?.length || 0;
    if (t === 'web-search') return this.selectedWebSearchHistories?.length || 0;
    if (t === 'link-analysis') return 0;

    return 0;
  }

  get deleteButtonLabel(): string {
    return this.selectedEntriesCount > 1 ? 'Delete all' : 'Delete';
  }

  private readonly allHistoryTabs: any[] = [
    { type: 'image', label: 'Image', isSelected: false },
    { type: 'face-compare', label: 'Face Compare', isSelected: false },
    { type: 'video-scanner', label: 'Video Scanner', isSelected: false },
    {
      type: 'osint-phone',
      label: 'Phone Number',
      isSelected: false,
    },
    { type: 'osint-email', label: 'Email', isSelected: false },
    { type: 'osint-username', label: 'Username', isSelected: false },
    { type: 'osint-name', label: 'Name', isSelected: false },
    { type: 'osint-wallet', label: 'Wallet', isSelected: false },
    {
      type: 'leak-phone',
      label: 'Leak Number',
      isSelected: false,
    },
    {
      type: 'leak-email',
      label: 'Leak Email',
      isSelected: false,
    },
    {
      type: 'leak-username',
      label: 'Leak Username',
      isSelected: false,
    },
    {
      type: 'leak-password',
      label: 'Leak Password',
      isSelected: false,
    },
    {
      type: 'vehicle',
      label: 'Vehicle',
      isSelected: false,
    },
    {
      type: 'pan',
      label: 'PAN',
      isSelected: false,
    },
    {
      type: 'gst',
      label: 'GST',
      isSelected: false,
    },
    {
      type: 'din',
      label: 'DIN',
      isSelected: false,
    },
    {
      type: 'mobile-pan',
      label: 'Mobile PAN',
      isSelected: false,
    },
    {
      type: 'phonepe_fastag',
      label: 'Fastag Search',
      isSelected: false,
    },
    {
      type: 'phonepe_upi',
      label: 'UPI to Bank',
      isSelected: false,
    },
    {
      type: 'virtual-number',
      label: 'Virtual Number',
      isSelected: false,
    },
    {
      type: 'advanced-search',
      label: 'Advanced Search',
      isSelected: false,
    },
    {
      type: 'basic-search',
      label: 'Basic Search',
      isSelected: false,
    },
    {
      type: 'web-search',
      label: 'Web Search',
      isSelected: false,
    },
    {
      type: 'link-analysis',
      label: 'Link Analysis',
      isSelected: false,
    },
  ];

  historyTabs: any = [...this.allHistoryTabs];

  selectedHistoryTab: any = {
    type: 'image',
    label: 'Image',
    isSelected: false,
  };

  // IMAGE HISTORIES

  imageHistories: any = [];
  imageHistoriesPaged: any = [];

  imageHistoriesCount: any = [];
  imageHistoriesPerPage = 12;
  imageHistoriesCurrentPage = 1;
  imageHistoriesHasPreviousPage: any;
  imageHistoriesHasNextPage: any;

  imageHistoriesSelectAll = { isSelected: false };

  imageFilters: any = [
    { label: 'Search Name', value: 'search-name' },
    { label: 'Created On', value: 'created-on' },
    { label: 'Status', value: 'status' },
  ];

  imageHistoryFilterKey: any = '';
  imageHistoryFilterValue: any = '';

  imageHistorySearchNameFilter: any = '';
  imageHistoryStartDateFilter: any = '';
  imageHistoryEndDateFilter: any = '';
  imageHistoryStatusFilter: any = 'all';

  selectedImageFilter: any = 'search-name';

  get selectedImageHistoriesPaged() {
    return this.imageHistoriesPaged
      .filter((imageHistoryPaged: any) => imageHistoryPaged.isSelected)
      .map((imageHistoryPaged: any) => imageHistoryPaged.search_id);
  }

  get imageHistoriesPagedStartAndEnd() {
    if (!this.imageHistories?.length || !this.imageHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.imageHistoriesCurrentPage - 1) * this.imageHistoriesPerPage + 1;
    const end = Math.min(
      start + this.imageHistories.length - 1,
      this.imageHistoriesCount,
    );

    return { start, end };
  }

  get selectedImageHistories() {
    return this.imageHistoriesPaged
      .filter((imageHistoryPaged: any) => imageHistoryPaged.isSelected)
      .map((imageHistoryPaged: any) => imageHistoryPaged.search_id);
  }

  // FACE COMPARE HISTORIES

  faceCompareHistories: any = [];
  faceCompareHistoriesPaged: any = [];

  faceCompareHistoriesCount: any = [];
  faceCompareHistoriesPerPage = 12;
  faceCompareHistoriesCurrentPage = 1;
  faceCompareHistoriesHasPreviousPage: any;
  faceCompareHistoriesHasNextPage: any;

  faceCompareHistoriesSelectAll = { isSelected: false };

  faceCompareFilters: any = [
    { label: 'File Name', value: 'file-name' },
    { label: 'Notes', value: 'notes' },
    { label: 'Created On', value: 'created-on' },
  ];

  faceCompareHistoryFilterKey: any = '';
  faceCompareHistoryFilterValue: any = '';

  faceCompareHistoryFileNameFilter: any = '';
  faceCompareHistoryNotesFilter: any = '';
  faceCompareHistoryStartDateFilter: any = '';
  faceCompareHistoryEndDateFilter: any = '';

  selectedFaceCompareFilter: any = 'file-name';

  showFaceCompareDetail = false;
  faceCompareDetailHistory: any = null;
  faceCompareDetailResults: any[] = [];
  faceCompareDetailLoading = false;

  get selectedFaceCompareHistories() {
    return this.faceCompareHistoriesPaged
      .filter((row: any) => row.isSelected)
      .map((row: any) => row.face_compare_id);
  }

  get faceCompareHistoriesPagedStartAndEnd() {
    if (!this.faceCompareHistories?.length || !this.faceCompareHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.faceCompareHistoriesCurrentPage - 1) *
        this.faceCompareHistoriesPerPage +
      1;
    const end = Math.min(
      start + this.faceCompareHistories.length - 1,
      this.faceCompareHistoriesCount,
    );

    return { start, end };
  }

  // VIDEO SCANNER HISTORIES

  videoScannerHistories: any = [];
  videoScannerHistoriesPaged: any = [];

  videoScannerHistoriesCount: any = [];
  videoScannerHistoriesPerPage = 12;
  videoScannerHistoriesCurrentPage = 1;
  videoScannerHistoriesHasPreviousPage: any;
  videoScannerHistoriesHasNextPage: any;

  videoScannerHistoriesSelectAll = { isSelected: false };

  videoScannerFilters: any = [
    { label: 'Video Name', value: 'video-name' },
    { label: 'Created On', value: 'created-on' },
  ];

  videoScannerHistoryFilterKey: any = '';
  videoScannerHistoryFilterValue: any = '';

  videoScannerHistoryVideoNameFilter: any = '';
  videoScannerHistoryStartDateFilter: any = '';
  videoScannerHistoryEndDateFilter: any = '';

  selectedVideoScannerFilter: any = 'video-name';

  showVideoScannerDetail = false;
  videoScannerDetailHistory: any = null;
  videoScannerDetailFrames: any[] = [];
  videoScannerDetailLoading = false;

  get selectedVideoScannerHistories() {
    return this.videoScannerHistoriesPaged
      .filter((row: any) => row.isSelected)
      .map((row: any) => row.video_scanner_id);
  }

  get videoScannerHistoriesPagedStartAndEnd() {
    if (
      !this.videoScannerHistories?.length ||
      !this.videoScannerHistoriesCount
    ) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.videoScannerHistoriesCurrentPage - 1) *
        this.videoScannerHistoriesPerPage +
      1;
    const end = Math.min(
      start + this.videoScannerHistories.length - 1,
      this.videoScannerHistoriesCount,
    );

    return { start, end };
  }

  // OSINT AND LEAK FILTERS

  osintAndLeakFilters: any = [];
  selectedOsintAndLeakFilter: any = 'id';

  // OSINT HISTORIES

  osintHistories: any = [];
  osintHistoriesPaged: any = [];

  osintHistoriesCount: any = [];
  osintHistoriesPerPage = 10;
  osintHistoriesCurrentPage = 1;
  osintHistoriesHasPreviousPage: any;
  osintHistoriesHasNextPage: any;

  osintHistoryFilters: any = {
    phone: [
      { label: 'Id', value: 'id' },
      { label: 'Phone', value: 'phone' },
      { label: 'Date Added', value: 'date-added' },
    ],
    email: [
      { label: 'Id', value: 'id' },
      { label: 'Email', value: 'email' },
      { label: 'Date Added', value: 'date-added' },
    ],
    username: [
      { label: 'Id', value: 'id' },
      { label: 'Username', value: 'username' },
      { label: 'Date Added', value: 'date-added' },
    ],
    name: [
      { label: 'Id', value: 'id' },
      { label: 'Name', value: 'name' },
      { label: 'Date Added', value: 'date-added' },
    ],
    wallet: [
      { label: 'Id', value: 'id' },
      { label: 'Wallet', value: 'wallet' },
      { label: 'Date Added', value: 'date-added' },
    ],
    gst: [
      { label: 'Id', value: 'id' },
      { label: 'GST', value: 'gst' },
      { label: 'Date Added', value: 'date-added' },
    ],
    din: [
      { label: 'Id', value: 'id' },
      { label: 'DIN', value: 'din' },
      { label: 'Date Added', value: 'date-added' },
    ],
  };

  osintHistoryFilterKey: any = '';
  osintHistoryFilterValue: any = '';

  osintHistoryIdFilter: any = '';
  osintHistoryPhoneFilter: any = '';
  osintHistoryEmailFilter: any = '';
  osintHistoryUsernameFilter: any = '';
  osintHistoryNameFilter: any = '';
  osintHistoryWalletFilter: any = '';
  osintHistoryGstFilter: any = '';
  osintHistoryDinFilter: any = '';
  osintHistoryStartDateFilter: any = '';
  osintHistoryEndDateFilter: any = '';

  osintHistoriesSelectAll = { isSelected: false };

  osintHistoriesShouldExport: any;
  osintHistoriesExportFormat: any;

  osintHistoriesTableThs: any = {
    phone: ['Id', 'Phone Number', 'Date Added', 'Time'],
    email: ['Id', 'Email', 'Date Added', 'Time'],
    username: ['Id', 'Username', 'Date Added', 'Time'],
    name: ['Id', 'Name', 'Date Added', 'Time'],
    wallet: ['Id', 'Wallet', 'Date Added', 'Time'],
    gst: ['Id', 'GSTIN', 'Date Added', 'Time'],
    din: ['Id', 'DIN', 'Date Added', 'Time'],
  };

  osintHistoriesTableTds: any = {
    phone: [
      { field: 'id' },
      { field: 'searched_phone' },
      { field: 'created_on', type: 'date' },
      { field: 'created_on', type: 'time' },
    ],
    email: [
      { field: 'id' },
      { field: 'searched_email' },
      { field: 'created_on', type: 'date' },
      { field: 'created_on', type: 'time' },
    ],
    username: [
      { field: 'id' },
      { field: 'searched_username' },
      { field: 'created_on', type: 'date' },
      { field: 'created_on', type: 'time' },
    ],
    name: [
      { field: 'id' },
      { field: 'searched_name' },
      { field: 'created_on', type: 'date' },
      { field: 'created_on', type: 'time' },
    ],
    wallet: [
      { field: 'id' },
      { field: 'searched_wallet' },
      { field: 'created_on', type: 'date' },
      { field: 'created_on', type: 'time' },
    ],
    gst: [
      { field: 'id' },
      { field: 'searched_gst' },
      { field: 'created_on', type: 'date' },
      { field: 'created_on', type: 'time' },
    ],
    din: [
      { field: 'id' },
      { field: 'searched_din' },
      { field: 'created_on', type: 'date' },
      { field: 'created_on', type: 'time' },
    ],
  };

  get selectedOsintHistoryType() {
    const historyType = this.selectedHistoryTab?.type;
    const historyTypeMap: Record<string, string> = {
      'osint-phone': 'phone',
      'osint-email': 'email',
      'osint-username': 'username',
      'osint-name': 'name',
      'osint-wallet': 'wallet',
    };

    return historyTypeMap[historyType] ? historyTypeMap[historyType] : '';
  }

  get selectedOsintHistoryTableThs() {
    return this.osintHistoriesTableThs[this.selectedOsintHistoryType] || [];
  }

  get selectedOsintHistoryTableTds() {
    return this.osintHistoriesTableTds[this.selectedOsintHistoryType] || [];
  }

  get osintHistoriesPagedStartAndEnd() {
    if (!this.osintHistories?.length || !this.osintHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.osintHistoriesCurrentPage - 1) * this.osintHistoriesPerPage + 1;
    const end = Math.min(
      start + this.osintHistories.length - 1,
      this.osintHistoriesCount,
    );

    return { start, end };
  }

  get selectedOsintHistories() {
    return this.osintHistoriesPaged
      .filter((osintHistoryPaged: any) => osintHistoryPaged.isSelected)
      .map((osintHistoryPaged: any) => osintHistoryPaged.osint_search_id);
  }

  // LEAK HISTORIES

  leakHistories: any = [];
  leakHistoriesPaged: any = [];

  leakHistoriesCount: any;
  leakHistoriesPerPage = 10;
  leakHistoriesCurrentPage = 1;
  leakHistoriesHasPreviousPage: any;
  leakHistoriesHasNextPage: any;

  leakHistoriesSelectAll = { isSelected: false };

  leakHistoryFilters: any = {
    phone: [
      { label: 'Id', value: 'id' },
      { label: 'Phone', value: 'phone' },
      { label: 'Date Added', value: 'date-added' },
    ],
    email: [
      { label: 'Id', value: 'id' },
      { label: 'Email', value: 'email' },
      { label: 'Date Added', value: 'date-added' },
    ],
    username: [
      { label: 'Id', value: 'id' },
      { label: 'Username', value: 'username' },
      { label: 'Date Added', value: 'date-added' },
    ],
    password: [
      { label: 'Id', value: 'id' },
      { label: 'Password', value: 'password' },
      { label: 'Date Added', value: 'date-added' },
    ],
  };

  leakHistoryFilterKey: any = '';
  leakHistoryFilterValue: any = '';

  leakHistoryIdFilter: any = '';
  leakHistoryPhoneFilter: any = '';
  leakHistoryEmailFilter: any = '';
  leakHistoryUsernameFilter: any = '';
  leakHistoryPasswordFilter: any = '';
  leakHistoryStartDateFilter: any = '';
  leakHistoryEndDateFilter: any = '';

  leakHistoriesShouldExport: any;
  leakHistoriesExportFormat: any;

  leakHistoryTableThs: any = {
    phone: ['Id', 'Phone Number', 'Date Added', 'Time'],
    email: ['Id', 'Email', 'Date Added', 'Time'],
    username: ['Id', 'Username', 'Date Added', 'Time'],
    password: ['Id', 'Password', 'Date Added', 'Time'],
  };

  leakHistoryTableTds: any = {
    phone: [
      { field: 'id' },
      { field: 'query' },
      { field: 'created_on', type: 'date' },
      { field: 'created_on', type: 'time' },
    ],
    email: [
      { field: 'id' },
      { field: 'query' },
      { field: 'created_on', type: 'date' },
      { field: 'created_on', type: 'time' },
    ],
    username: [
      { field: 'id' },
      { field: 'query' },
      { field: 'created_on', type: 'date' },
      { field: 'created_on', type: 'time' },
    ],
    password: [
      { field: 'id' },
      { field: 'query' },
      { field: 'created_on', type: 'date' },
      { field: 'created_on', type: 'time' },
    ],
  };

  get selectedLeakHistoryType() {
    const historyType = this.selectedHistoryTab?.type;

    const historyTypeMap: Record<string, string> = {
      'leak-phone': 'phone',
      'leak-email': 'email',
      'leak-username': 'username',
      'leak-password': 'password',
    };

    return historyTypeMap[historyType] ? historyTypeMap[historyType] : '';
  }

  get selectedLeakHistoryTableThs() {
    return this.leakHistoryTableThs[this.selectedLeakHistoryType] || [];
  }

  get selectedLeakHistoryTableTds() {
    return this.leakHistoryTableTds[this.selectedLeakHistoryType] || [];
  }

  get leakHistoriesPagedStartAndEnd() {
    if (!this.leakHistories?.length || !this.leakHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.leakHistoriesCurrentPage - 1) * this.leakHistoriesPerPage + 1;
    const end = Math.min(
      start + this.leakHistories.length - 1,
      this.leakHistoriesCount,
    );

    return { start, end };
  }

  get selectedLeakHistories() {
    return this.leakHistoriesPaged
      .filter((leakHistoryPaged: any) => leakHistoryPaged.isSelected)
      .map((leakHistoryPaged: any) => leakHistoryPaged.leak_search_id);
  }

  // VEHICLE HISTORIES

  vehicleHistories: any = [];
  vehicleHistoriesPaged: any = [];

  vehicleHistoriesCount: any;
  vehicleHistoriesPerPage = 10;
  vehicleHistoriesCurrentPage = 1;
  vehicleHistoriesHasPreviousPage: any;
  vehicleHistoriesHasNextPage: any;

  vehicleHistoriesSelectAll = { isSelected: false };

  vehicleHistoryFilters: any = [
    { label: 'Id', value: 'id' },
    { label: 'Vehicle Number', value: 'vehicle-number' },
    { label: 'Date Added', value: 'date-added' },
  ];

  vehicleHistoryFilterKey: any = '';
  vehicleHistoryFilterValue: any = '';

  vehicleHistoryIdFilter: any = '';
  vehicleHistoryVehicleNumberFilter: any = '';
  vehicleHistoryStartDateFilter: any = '';
  vehicleHistoryEndDateFilter: any = '';

  vehicleHistoriesShouldExport: any;
  vehicleHistoriesExportFormat: any;

  vehicleHistoryTableThs: any = ['Id', 'Vehicle Number', 'Date Added', 'Time'];

  vehicleHistoryTableTds: any = [
    { field: 'id' },
    { field: 'vehicle_number' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get vehicleHistoriesPagedStartAndEnd() {
    if (!this.vehicleHistories?.length || !this.vehicleHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.vehicleHistoriesCurrentPage - 1) * this.vehicleHistoriesPerPage + 1;
    const end = Math.min(
      start + this.vehicleHistories.length - 1,
      this.vehicleHistoriesCount,
    );

    return { start, end };
  }

  get selectedVehicleHistories() {
    return this.vehicleHistoriesPaged
      .filter((vehicleHistoryPaged: any) => vehicleHistoryPaged.isSelected)
      .map((vehicleHistoryPaged: any) => vehicleHistoryPaged.vehicle_search_id);
  }

  // PAN HISTORIES

  panHistories: any = [];
  panHistoriesPaged: any = [];

  panHistoriesCount: any;
  panHistoriesPerPage = 10;
  panHistoriesCurrentPage = 1;
  panHistoriesHasPreviousPage: any;
  panHistoriesHasNextPage: any;

  panHistoriesSelectAll = { isSelected: false };

  panHistoryFilters: any = [
    { label: 'Id', value: 'id' },
    { label: 'PAN Number', value: 'pan-number' },
    { label: 'Date Added', value: 'date-added' },
  ];

  panHistoryFilterKey: any = '';
  panHistoryFilterValue: any = '';

  panHistoryIdFilter: any = '';
  panHistoryPanNumberFilter: any = '';
  panHistoryStartDateFilter: any = '';
  panHistoryEndDateFilter: any = '';

  panHistoriesShouldExport: any;
  panHistoriesExportFormat: any;

  panHistoryTableThs: any = ['Id', 'PAN Number', 'Date Added', 'Time'];

  panHistoryTableTds: any = [
    { field: 'id' },
    { field: 'pan_number' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get panHistoriesPagedStartAndEnd() {
    if (!this.panHistories?.length || !this.panHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.panHistoriesCurrentPage - 1) * this.panHistoriesPerPage + 1;
    const end = Math.min(
      start + this.panHistories.length - 1,
      this.panHistoriesCount,
    );

    return { start, end };
  }

  get selectedPanHistories() {
    return this.panHistoriesPaged
      .filter((panHistoryPaged: any) => panHistoryPaged.isSelected)
      .map((panHistoryPaged: any) => panHistoryPaged.pan_search_id);
  }

  // GST HISTORIES

  gstHistories: any = [];
  gstHistoriesPaged: any = [];

  gstHistoriesCount: any;
  gstHistoriesPerPage = 10;
  gstHistoriesCurrentPage = 1;
  gstHistoriesHasPreviousPage: any;
  gstHistoriesHasNextPage: any;

  gstHistoriesSelectAll = { isSelected: false };

  gstHistoryFilters: any = [
    { label: 'Id', value: 'id' },
    { label: 'GSTIN', value: 'gst' },
    { label: 'Date Added', value: 'date-added' },
  ];

  gstHistoryFilterKey: any = '';
  gstHistoryFilterValue: any = '';

  gstHistoryIdFilter: any = '';
  gstHistoryGstFilter: any = '';
  gstHistoryStartDateFilter: any = '';
  gstHistoryEndDateFilter: any = '';

  gstHistoriesShouldExport: any;
  gstHistoriesExportFormat: any;

  gstHistoryTableThs: any = ['Id', 'GSTIN', 'Date Added', 'Time'];

  gstHistoryTableTds: any = [
    { field: 'id' },
    { field: 'searched_gst' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get gstHistoriesPagedStartAndEnd() {
    if (!this.gstHistories?.length || !this.gstHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.gstHistoriesCurrentPage - 1) * this.gstHistoriesPerPage + 1;
    const end = Math.min(
      start + this.gstHistories.length - 1,
      this.gstHistoriesCount,
    );

    return { start, end };
  }

  get selectedGstHistories() {
    return this.gstHistoriesPaged
      .filter((gstHistoryPaged: any) => gstHistoryPaged.isSelected)
      .map((gstHistoryPaged: any) => gstHistoryPaged.gst_search_id);
  }

  // DIN HISTORIES

  dinHistories: any = [];
  dinHistoriesPaged: any = [];

  dinHistoriesCount: any;
  dinHistoriesPerPage = 10;
  dinHistoriesCurrentPage = 1;
  dinHistoriesHasPreviousPage: any;
  dinHistoriesHasNextPage: any;

  dinHistoriesSelectAll = { isSelected: false };

  dinHistoryFilters: any = [
    { label: 'Id', value: 'id' },
    { label: 'DIN', value: 'din' },
    { label: 'Date Added', value: 'date-added' },
  ];

  dinHistoryFilterKey: any = '';
  dinHistoryFilterValue: any = '';

  dinHistoryIdFilter: any = '';
  dinHistoryDinFilter: any = '';
  dinHistoryStartDateFilter: any = '';
  dinHistoryEndDateFilter: any = '';

  dinHistoriesShouldExport: any;
  dinHistoriesExportFormat: any;

  dinHistoryTableThs: any = ['Id', 'DIN', 'Date Added', 'Time'];

  dinHistoryTableTds: any = [
    { field: 'id' },
    { field: 'searched_din' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get dinHistoriesPagedStartAndEnd() {
    if (!this.dinHistories?.length || !this.dinHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.dinHistoriesCurrentPage - 1) * this.dinHistoriesPerPage + 1;
    const end = Math.min(
      start + this.dinHistories.length - 1,
      this.dinHistoriesCount,
    );

    return { start, end };
  }

  get selectedDinHistories() {
    return this.dinHistoriesPaged
      .filter((dinHistoryPaged: any) => dinHistoryPaged.isSelected)
      .map((dinHistoryPaged: any) => dinHistoryPaged.din_search_id);
  }


  // BASIC SEARCH HISTORIES

  basicSearchHistories: any = [];
  basicSearchHistoriesPaged: any = [];

  basicSearchHistoriesCount: any;
  basicSearchHistoriesPerPage = 10;
  basicSearchHistoriesCurrentPage = 1;
  basicSearchHistoriesHasPreviousPage: any;
  basicSearchHistoriesHasNextPage: any;

  basicSearchHistoriesSelectAll = { isSelected: false };

  basicSearchHistoryFilters: any = [
    { label: 'Phone', value: 'phone' },
    { label: 'Date Added', value: 'date-added' },
  ];

  basicSearchHistoryFilterKey: any = '';
  basicSearchHistoryFilterValue: any = '';

  basicSearchHistoryPhoneFilter: any = '';
  basicSearchHistoryStartDateFilter: any = '';
  basicSearchHistoryEndDateFilter: any = '';

  basicSearchHistoriesShouldExport: any;
  basicSearchHistoriesExportFormat: any;

  basicSearchHistoryTableThs: any = ['Id', 'Phone Number', 'Date Added', 'Time'];

  basicSearchHistoryTableTds: any = [
    { field: 'id' },
    { field: 'searched_phone' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get basicSearchHistoriesPagedStartAndEnd() {
    if (!this.basicSearchHistories?.length || !this.basicSearchHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.basicSearchHistoriesCurrentPage - 1) *
        this.basicSearchHistoriesPerPage +
      1;
    const end = Math.min(
      start + this.basicSearchHistories.length - 1,
      this.basicSearchHistoriesCount,
    );

    return { start, end };
  }

  get selectedBasicSearchHistories() {
    return this.basicSearchHistoriesPaged
      .filter((row: any) => row.isSelected)
      .map((row: any) => row.basic_search_id);
  }

  // WEB SEARCH HISTORIES

  webSearchHistories: any = [];
  webSearchHistoriesPaged: any = [];

  webSearchHistoriesCount: any;
  webSearchHistoriesPerPage = 10;
  webSearchHistoriesCurrentPage = 1;
  webSearchHistoriesHasPreviousPage: any;
  webSearchHistoriesHasNextPage: any;

  webSearchHistoriesSelectAll = { isSelected: false };

  webSearchHistoryFilters: any = [
    { label: 'URL', value: 'url' },
    { label: 'Domain', value: 'domain' },
    { label: 'Date Added', value: 'date-added' },
  ];

  webSearchHistoryFilterKey: any = '';
  webSearchHistoryFilterValue: any = '';

  webSearchHistoryUrlFilter: any = '';
  webSearchHistoryDomainFilter: any = '';
  webSearchHistoryStartDateFilter: any = '';
  webSearchHistoryEndDateFilter: any = '';

  webSearchHistoriesShouldExport: any;
  webSearchHistoriesExportFormat: any;

  webSearchHistoryTableThs: any = ['Id', 'URL', 'Domain', 'Date Added', 'Time'];

  webSearchHistoryTableTds: any = [
    { field: 'id' },
    { field: 'url' },
    { field: 'domain' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get webSearchHistoriesPagedStartAndEnd() {
    if (!this.webSearchHistories?.length || !this.webSearchHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.webSearchHistoriesCurrentPage - 1) * this.webSearchHistoriesPerPage +
      1;
    const end = Math.min(
      start + this.webSearchHistories.length - 1,
      this.webSearchHistoriesCount,
    );

    return { start, end };
  }

  get selectedWebSearchHistories() {
    return this.webSearchHistoriesPaged
      .filter((row: any) => row.isSelected)
      .map((row: any) => row.web_capture_id);
  }

  // CONSTRUCTOR

  constructor(
    private searchHistoryService: SearchHistoryService,
    private router: Router,
  ) { }

  // LIFECYCLE AND COMMON METHODS

  ngOnInit() {
    this.retrieveSubscriptionDetails();
    this.initializeHistoryTabs();

    // Set up debounced subscriptions
    this.subscriptions.push(
      this.imageHistorySearchNameSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onImageHistorySearchNameFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.faceCompareHistoryFileNameSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) =>
          this.onFaceCompareHistoryFileNameFilterChangeDebounced(value),
        )
    );

    this.subscriptions.push(
      this.faceCompareHistoryNotesSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) =>
          this.onFaceCompareHistoryNotesFilterChangeDebounced(value),
        )
    );

    this.subscriptions.push(
      this.videoScannerHistoryVideoNameSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) =>
          this.onVideoScannerHistoryVideoNameFilterChangeDebounced(value),
        )
    );

    this.subscriptions.push(
      this.osintHistoryPhoneSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onOsintHistoryPhoneFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.osintHistoryEmailSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onOsintHistoryEmailFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.osintHistoryUsernameSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onOsintHistoryUsernameFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.osintHistoryNameSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onOsintHistoryNameFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.osintHistoryWalletSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onOsintHistoryWalletFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.osintHistoryGstSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onOsintHistoryGstFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.osintHistoryDinSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onOsintHistoryDinFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.leakHistoryPhoneSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onLeakHistoryPhoneFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.leakHistoryEmailSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onLeakHistoryEmailFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.leakHistoryUsernameSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onLeakHistoryUsernameFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.leakHistoryPasswordSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onLeakHistoryPasswordFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.vehicleHistoryIdSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onVehicleHistoryIdFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.vehicleHistoryVehicleNumberSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onVehicleHistoryVehicleNumberFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.panHistoryIdSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onPanHistoryIdFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.panHistoryPanNumberSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onPanHistoryPanNumberFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.virtualNumberHistoryIdSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onVirtualNumberHistoryIdFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.virtualNumberHistoryVirtualNumberSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onVirtualNumberHistoryVirtualNumberFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.advancedSearchHistoryIdSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onAdvancedSearchHistoryIdFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.advancedSearchHistorySearchNameSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onAdvancedSearchHistorySearchNameFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.basicSearchHistoryPhoneSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onBasicSearchHistoryPhoneFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.webSearchHistoryUrlSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onWebSearchHistoryUrlFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.webSearchHistoryDomainSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onWebSearchHistoryDomainFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.linkAnalysisRootValueSubject
        .pipe(debounceTime(900), distinctUntilChanged())
        .subscribe((value) => this.onLinkAnalysisRootValueFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.linkAnalysisRootTypeSubject
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((value) => this.onLinkAnalysisRootTypeFilterChangeDebounced(value))
    );

    this.subscriptions.push(
      this.linkAnalysisStatusSubject
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((value) => this.onLinkAnalysisStatusFilterChangeDebounced(value))
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  retrieveSubscriptionDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;
    this.subscriptionDetails = userPortal?.subscriptionDetails || {};
  }

  initializeHistoryTabs() {
    const tabVisibilityChecks = this.allHistoryTabs.map((tab) =>
      this.shouldShowHistoryTab(tab.type).pipe(
        map((shouldShow) => ({ ...tab, shouldShow })),
      ),
    );

    this.subscriptions.push(
      forkJoin(tabVisibilityChecks).subscribe((tabsWithVisibility) => {
        this.historyTabs = tabsWithVisibility
          .filter((tab) => tab.shouldShow)
          .map(({ shouldShow, ...tab }) => ({ ...tab, isSelected: false }));

        if (!this.historyTabs.length) {
          this.selectedHistoryTab = { type: '', label: '', isSelected: false };
          return;
        }

        this.onHistoryTypeTabClick(this.historyTabs[0]);
      }),
    );
  }

  shouldShowHistoryTab(tabType: string): Observable<boolean> {
    if (this.hasPermissionForHistoryTab(tabType)) {
      return of(true);
    }

    return this.hasExistingHistoryForTab(tabType);
  }

  hasPermissionForHistoryTab(tabType: string): boolean {
    const subscription = this.subscriptionDetails || {};

    switch (tabType) {
      case 'image':
        return Boolean(subscription?.face_search_permission);
      case 'face-compare':
        return Boolean(subscription?.face_compare_permission);
      case 'video-scanner':
        return Boolean(subscription?.video_scanner_permission);
      case 'osint-phone':
      case 'osint-email':
      case 'osint-username':
      case 'osint-name':
      case 'osint-wallet':
        return Boolean(subscription?.osint_search_permission);
      case 'gst':
        return Boolean(subscription?.gst_search_permission);
      case 'din':
        return Boolean(subscription?.din_search_permission);
      case 'mobile-pan':
        return Boolean(subscription?.mobile_pan_search_permission);
      case 'phonepe_fastag':
        return Boolean(subscription?.phonepe_fastag_search_permission);
      case 'phonepe_upi':
        return Boolean(subscription?.phonepe_upi_search_permission);
      case 'leak-phone':
      case 'leak-email':
      case 'leak-username':
      case 'leak-password':
        return Boolean(subscription?.leak_search_permission);
      case 'vehicle':
        return Boolean(subscription?.vehicle_search_permission);
      case 'pan':
        return Boolean(subscription?.pan_search_permission);
      case 'virtual-number':
        return Boolean(subscription?.virtual_number_permission);
      case 'advanced-search':
        return Boolean(subscription?.name_search_permission);
      case 'basic-search':
        return subscription?.basic_search_permission !== false;
      case 'web-search':
        return Boolean(subscription?.web_capture_permission);
      case 'link-analysis':
        return Boolean(
          subscription?.osint_search_permission ||
          subscription?.leak_search_permission,
        );
      default:
        return false;
    }
  }

  hasExistingHistoryForTab(tabType: string): Observable<boolean> {
    const page = 1;
    const size = 1;

    switch (tabType) {
      case 'image':
        return this.searchHistoryService
          .getImageHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'face-compare':
        return this.searchHistoryService
          .getFaceCompareHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'video-scanner':
        return this.searchHistoryService
          .getVideoScannerHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'osint-phone':
      case 'osint-email':
      case 'osint-username':
      case 'osint-name':
      case 'osint-wallet':
        return this.searchHistoryService
          .getOsintHistories(tabType.replace('osint-', ''), page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'leak-phone':
      case 'leak-email':
      case 'leak-username':
      case 'leak-password':
        return this.searchHistoryService
          .getLeakHistories(tabType.replace('leak-', ''), page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'vehicle':
        return this.searchHistoryService
          .getVehicleHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'pan':
        return this.searchHistoryService
          .getPanHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'gst':
        return this.searchHistoryService
          .getGstHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'din':
        return this.searchHistoryService
          .getDinHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'mobile-pan':
        return this.searchHistoryService
          .getMobilePanHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'phonepe_fastag':
        return this.searchHistoryService
          .getPhonepeFastagHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'phonepe_upi':
        return this.searchHistoryService
          .getPhonepeUpiHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'virtual-number':
        return this.searchHistoryService
          .getVirtualNumberHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'advanced-search':
        return this.searchHistoryService
          .getAdvancedSearchHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'basic-search':
        return this.searchHistoryService
          .getBasicSearchHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || response?.data?.total_count || 0) > 0),
            catchError(() => of(false)),
          );
      case 'web-search':
        return this.searchHistoryService
          .getWebSearchHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      case 'link-analysis':
        return this.searchHistoryService
          .getLinkAnalysisHistories(page, size)
          .pipe(
            map((response) => Number(response?.data?.total_items || 0) > 0),
            catchError(() => of(false)),
          );
      default:
        return of(false);
    }
  }

  addAdditionalKeys(histories: any) {
    return histories.map((history: any, index: any) => ({
      ...history,
      id: index + 1,
      isSelected: false,
    }));
  }

  // HISTORY TYPE TABS

  onHistoryTypeTabClick(selectedHistoryTypeTab: any) {
    this.selectedHistoryTab = selectedHistoryTypeTab;
    this.updateHistoryTabs();

    this.osintHistoriesSelectAll.isSelected = false;
    this.leakHistoriesSelectAll.isSelected = false;
    this.vehicleHistoriesSelectAll.isSelected = false;
    this.panHistoriesSelectAll.isSelected = false;
    this.gstHistoriesSelectAll.isSelected = false;
    this.dinHistoriesSelectAll.isSelected = false;
    this.mobilePanHistoriesSelectAll.isSelected = false;
    this.phonepeFastagHistoriesSelectAll.isSelected = false;
    this.phonepeUpiHistoriesSelectAll.isSelected = false;
    this.virtualNumberHistoriesSelectAll.isSelected = false;
    this.advancedSearchHistoriesSelectAll.isSelected = false;
    this.basicSearchHistoriesSelectAll.isSelected = false;
    this.webSearchHistoriesSelectAll.isSelected = false;
    this.faceCompareHistoriesSelectAll.isSelected = false;
    this.videoScannerHistoriesSelectAll.isSelected = false;
    this.closeFaceCompareDetail();
    this.closeVideoScannerDetail();

    if (this.selectedHistoryTab.type === 'image') {
      this.updateImageHistoryFilter();
      this.resetImageHistories();
      this.getImageHistories();
    }

    if (this.selectedHistoryTab.type === 'face-compare') {
      this.updateFaceCompareHistoryFilter();
      this.resetFaceCompareHistories();
      this.getFaceCompareHistories();
    }

    if (this.selectedHistoryTab.type === 'video-scanner') {
      this.updateVideoScannerHistoryFilter();
      this.resetVideoScannerHistories();
      this.getVideoScannerHistories();
    }

    if (
      this.selectedHistoryTab.type === 'osint-phone' ||
      this.selectedHistoryTab.type === 'osint-email' ||
      this.selectedHistoryTab.type === 'osint-username' ||
      this.selectedHistoryTab.type === 'osint-name' ||
      this.selectedHistoryTab.type === 'osint-wallet'
    ) {
      this.updateOsintAndLeakHistoryFilters();
      this.resetOsintHistories();
      this.getOsintHistories();
    }

    if (
      this.selectedHistoryTab.type === 'leak-phone' ||
      this.selectedHistoryTab.type === 'leak-email' ||
      this.selectedHistoryTab.type === 'leak-username' ||
      this.selectedHistoryTab.type === 'leak-password'
    ) {
      this.updateOsintAndLeakHistoryFilters();
      this.resetLeakHistories();
      this.getLeakHistories();
    }

    if (this.selectedHistoryTab.type === 'vehicle') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetVehicleHistories();
      this.getVehicleHistories();
    }

    if (this.selectedHistoryTab.type === 'pan') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetPanHistories();
      this.getPanHistories();
    }

    if (this.selectedHistoryTab.type === 'gst') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetGstHistories();
      this.getGstHistories();
    }

    if (this.selectedHistoryTab.type === 'din') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetDinHistories();
      this.getDinHistories();
    }

    if (this.selectedHistoryTab.type === 'mobile-pan') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetMobilePanHistories();
      this.getMobilePanHistories();
    }

    if (this.selectedHistoryTab.type === 'phonepe_fastag') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetPhonepeFastagHistories();
      this.getPhonepeFastagHistories();
    }

    if (this.selectedHistoryTab.type === 'phonepe_upi') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetPhonepeUpiHistories();
      this.getPhonepeUpiHistories();
    }

    if (this.selectedHistoryTab.type === 'virtual-number') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetVirtualNumberHistories();
      this.getVirtualNumberHistories();
    }

    if (this.selectedHistoryTab.type === 'advanced-search') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetAdvancedSearchHistories();
      this.getAdvancedSearchHistories();
    }

    if (this.selectedHistoryTab.type === 'basic-search') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetBasicSearchHistories();
      this.getBasicSearchHistories();
    }

    if (this.selectedHistoryTab.type === 'web-search') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetWebSearchHistories();
      this.getWebSearchHistories();
    }

    if (this.selectedHistoryTab.type === 'link-analysis') {
      this.updateOsintAndLeakHistoryFilters();
      this.resetLinkAnalysisHistories();
      this.getLinkAnalysisHistories();
    }
  }

  updateHistoryTabs() {
    this.historyTabs = this.historyTabs.map((historyTab: any) => ({
      ...historyTab,
      isSelected:
        historyTab.type === this.selectedHistoryTab.type ? true : false,
    }));
  }

  // IMAGE HISTORIES

  imageHistoriesFormatDate(date: any) {
    if (!date) {
      return '';
    }
    const isoDate = parseISO(date);
    return format(isoDate, 'dd MMM yy | hh : mm a');
  }

  onImageHistoriesPageChange(page: any) {
    this.imageHistoriesSelectAll.isSelected = false;
    this.imageHistoriesCurrentPage = page;

    this.getImageHistories();
  }

  onImageHistoryPagedClick(event: Event, imageHistoryPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateImageHistoryPaged(imageHistoryPaged.search_id, isChecked);
  }

  onImageHistoriesDeleteButtonClick() {
    this.pendingDeleteAction = 'image';
    this.showDeleteConfirmation = true;
  }

  updateImageHistoryPaged(searchId: any, isSelected: any) {
    const selectedImageHistoryPaged = this.imageHistoriesPaged.find(
      (imageHistoryPaged: any) => imageHistoryPaged.search_id === searchId,
    );

    if (selectedImageHistoryPaged) {
      selectedImageHistoryPaged.isSelected = isSelected;
    }
  }

  getImageHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getImageHistories(
        this.imageHistoriesCurrentPage,
        this.imageHistoriesPerPage,
        this.imageHistoryFilterKey,
        this.imageHistoryFilterValue,
        this.imageHistoryStartDateFilter,
        this.imageHistoryEndDateFilter,
      )
      .subscribe({
        next: (response) => {
          // console.log('GET IMAGE HISTORIES API RESPONSE: ', response);

          this.imageHistories = response?.data?.data_list;
          this.imageHistoriesPaged = this.addAdditionalKeys([
            ...this.imageHistories,
          ]);
          this.imageHistoriesCount = response?.data?.total_items;
          this.imageHistoriesHasPreviousPage = response?.data?.has_previous;
          this.imageHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  deleteImageHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deleteImageHistories(this.selectedImageHistories)
      .subscribe({
        next: (response) => {
          // console.log('DELETE IMAGE HISTORIES API RESPONSE: ', response);

          this.imageHistoriesSelectAll.isSelected = false;

          this.updateImageHistoriesPaged();
          this.updateImageHistoriesCurrentPage();

          this.getImageHistories();
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  updateImageHistoriesPaged() {
    this.imageHistoriesPaged = this.imageHistoriesPaged.filter(
      (imageHistoryPaged: any) =>
        !this.selectedImageHistories.includes(imageHistoryPaged.search_id),
    );
  }

  updateImageHistoriesCurrentPage() {
    if (this.imageHistoriesPaged.length === 0) {
      if (this.imageHistoriesHasNextPage) {
      } else {
        if (this.imageHistoriesHasPreviousPage) {
          this.imageHistoriesCurrentPage--;
        }
      }
    }
  }

  onImageHistoriesSelectAllButtonClick() {
    this.imageHistoriesSelectAll.isSelected =
      !this.imageHistoriesSelectAll.isSelected;

    this.imageHistoriesSelectAll.isSelected
      ? this.selectAllImageHistoriesPaged()
      : this.deselectAllImageHistoriesPaged();
  }

  selectAllImageHistoriesPaged() {
    this.imageHistoriesPaged = this.imageHistoriesPaged.map(
      (imageHistoryPaged: any) => ({
        ...imageHistoryPaged,
        isSelected: true,
      }),
    );
  }

  deselectAllImageHistoriesPaged() {
    this.imageHistoriesPaged = this.imageHistoriesPaged.map(
      (imageHistoryPaged: any) => ({
        ...imageHistoryPaged,
        isSelected: false,
      }),
    );
  }

  onImageFilterChange() {
    this.resetImageHistories();
  }

  resetImageHistories() {
    this.imageHistoriesCurrentPage = 1;

    this.imageHistoryFilterKey = '';
    this.imageHistoryFilterValue = '';

    this.imageHistorySearchNameFilter = '';
    this.imageHistoryStartDateFilter = '';
    this.imageHistoryEndDateFilter = '';
    this.imageHistoryStatusFilter = 'all';
  }

  onImageHistorySearchNameFilterChange(searchName: any) {
    this.imageHistorySearchNameSubject.next(searchName);
  }

  private onImageHistorySearchNameFilterChangeDebounced(searchName: any) {
    const trimmedSearchName = searchName.trim();

    if (trimmedSearchName === '') {
      this.imageHistoryFilterKey = '';
      this.imageHistoryFilterValue = '';

      this.getImageHistories();
    } else if (trimmedSearchName.length === 1) {
      return;
    } else {
      this.imageHistoryFilterKey = 'job_name';
      this.imageHistoryFilterValue = trimmedSearchName;

      this.getImageHistories();
    }
  }

  onImageHistoryStartDateFilterChange(startDate: any) {
    this.imageHistoryStartDateFilter = startDate;

    if (
      this.imageHistoryStartDateFilter === '' &&
      this.imageHistoryEndDateFilter === ''
    ) {
      this.imageHistoryFilterKey = '';
      this.imageHistoryFilterValue = '';

      this.getImageHistories();
    }

    if (this.imageHistoryStartDateFilter && this.imageHistoryEndDateFilter) {
      this.imageHistoryFilterKey = 'created_on';
      this.getImageHistories();
    }
  }

  onImageHistoryEndDateFilterChange(endDate: any) {
    this.imageHistoryEndDateFilter = endDate;

    if (
      this.imageHistoryStartDateFilter === '' &&
      this.imageHistoryEndDateFilter === ''
    ) {
      this.imageHistoryFilterKey = '';
      this.imageHistoryFilterValue = '';

      this.getImageHistories();
    }

    if (this.imageHistoryStartDateFilter && this.imageHistoryEndDateFilter) {
      this.imageHistoryFilterKey = 'created_on';
      this.getImageHistories();
    }
  }

  onImageHistoryStatusFilterChange(status: any) {
    this.imageHistoryStatusFilter = status;

    if (this.imageHistoryStatusFilter === 'all') {
      this.imageHistoryFilterKey = '';
      this.imageHistoryFilterValue = '';

      this.getImageHistories();
    }

    if (this.imageHistoryStatusFilter === 'success') {
      this.imageHistoryFilterKey = 'status';
      this.imageHistoryFilterValue = 'success';

      this.getImageHistories();
    }

    if (this.imageHistoryStatusFilter === 'pending') {
      this.imageHistoryFilterKey = 'status';
      this.imageHistoryFilterValue = 'pending';

      this.getImageHistories();
    }
  }

  updateImageHistoryFilter() {
    this.selectedImageFilter = 'search-name';
  }

  onImageHistoryHeadshotClick(imageHistoryPaged: any) {
    this.router.navigate(['/osint-image-search-result'], {
      state: {
        staleSearchId: imageHistoryPaged.search_id,
        isStaleResult: true,
        selectedImageDetails: {
          thumbnail: imageHistoryPaged.thumbnail,
          fid: imageHistoryPaged.search_id, // Using search_id as fid for stale results
        },
        jobId: imageHistoryPaged.job_id,
        searchName: imageHistoryPaged.job_name && imageHistoryPaged.job_name !== 'New Search'
          ? imageHistoryPaged.job_name
          : 'Face Search',
      },
    });
  }

  // FACE COMPARE HISTORIES

  faceCompareHistoriesFormatDate(date: any) {
    return this.imageHistoriesFormatDate(date);
  }

  formatFaceCompareProbability(probability: any): string {
    if (probability === null || probability === undefined || probability === '') {
      return '—';
    }
    const value = Number(probability);
    if (!Number.isFinite(value)) {
      return '—';
    }
    // Backend stores 0–1 scores; tolerate 0–100 already-scaled values
    const percent = value <= 1 ? value * 100 : value;
    return `${percent.toFixed(1)}%`;
  }

  formatFaceCompareMatch(match: any): string {
    if (match === true) return 'Match';
    if (match === false) return 'No match';
    return '—';
  }

  onFaceCompareHistoriesPageChange(page: any) {
    this.faceCompareHistoriesSelectAll.isSelected = false;
    this.faceCompareHistoriesCurrentPage = page;
    this.getFaceCompareHistories();
  }

  onFaceCompareHistoryPagedClick(event: Event, row: any) {
    event.stopPropagation();
    const checkbox = event.target as HTMLInputElement;
    this.updateFaceCompareHistoryPaged(row.face_compare_id, checkbox.checked);
  }

  onFaceCompareHistoriesDeleteButtonClick() {
    this.pendingDeleteAction = 'face-compare';
    this.showDeleteConfirmation = true;
  }

  updateFaceCompareHistoryPaged(faceCompareId: any, isSelected: any) {
    const selected = this.faceCompareHistoriesPaged.find(
      (row: any) => row.face_compare_id === faceCompareId,
    );
    if (selected) {
      selected.isSelected = isSelected;
    }
  }

  getFaceCompareHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getFaceCompareHistories(
        this.faceCompareHistoriesCurrentPage,
        this.faceCompareHistoriesPerPage,
        this.faceCompareHistoryFilterKey,
        this.faceCompareHistoryFilterValue,
        this.faceCompareHistoryStartDateFilter,
        this.faceCompareHistoryEndDateFilter,
      )
      .subscribe({
        next: (response) => {
          this.faceCompareHistories = response?.data?.data_list || [];
          this.faceCompareHistoriesPaged = this.addAdditionalKeys([
            ...this.faceCompareHistories,
          ]);
          this.faceCompareHistoriesCount = response?.data?.total_items || 0;
          this.faceCompareHistoriesHasPreviousPage =
            response?.data?.has_previous;
          this.faceCompareHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  deleteFaceCompareHistories() {
    if (!this.selectedFaceCompareHistories?.length) {
      return;
    }

    this.isLoading = true;
    this.searchHistoryService
      .deleteFaceCompareHistories(this.selectedFaceCompareHistories)
      .subscribe({
        next: () => {
          this.faceCompareHistoriesSelectAll.isSelected = false;
          this.updateFaceCompareHistoriesPaged();
          this.updateFaceCompareHistoriesCurrentPage();
          this.getFaceCompareHistories();
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  updateFaceCompareHistoriesPaged() {
    this.faceCompareHistoriesPaged = this.faceCompareHistoriesPaged.filter(
      (row: any) =>
        !this.selectedFaceCompareHistories.includes(row.face_compare_id),
    );
  }

  updateFaceCompareHistoriesCurrentPage() {
    if (this.faceCompareHistoriesPaged.length === 0) {
      if (!this.faceCompareHistoriesHasNextPage) {
        if (this.faceCompareHistoriesHasPreviousPage) {
          this.faceCompareHistoriesCurrentPage--;
        }
      }
    }
  }

  onFaceCompareHistoriesSelectAllButtonClick() {
    this.faceCompareHistoriesSelectAll.isSelected =
      !this.faceCompareHistoriesSelectAll.isSelected;

    this.faceCompareHistoriesSelectAll.isSelected
      ? this.selectAllFaceCompareHistoriesPaged()
      : this.deselectAllFaceCompareHistoriesPaged();
  }

  selectAllFaceCompareHistoriesPaged() {
    this.faceCompareHistoriesPaged = this.faceCompareHistoriesPaged.map(
      (row: any) => ({
        ...row,
        isSelected: true,
      }),
    );
  }

  deselectAllFaceCompareHistoriesPaged() {
    this.faceCompareHistoriesPaged = this.faceCompareHistoriesPaged.map(
      (row: any) => ({
        ...row,
        isSelected: false,
      }),
    );
  }

  onFaceCompareFilterChange() {
    this.resetFaceCompareHistories();
  }

  resetFaceCompareHistories() {
    this.faceCompareHistoriesCurrentPage = 1;
    this.faceCompareHistoryFilterKey = '';
    this.faceCompareHistoryFilterValue = '';
    this.faceCompareHistoryFileNameFilter = '';
    this.faceCompareHistoryNotesFilter = '';
    this.faceCompareHistoryStartDateFilter = '';
    this.faceCompareHistoryEndDateFilter = '';
  }

  onFaceCompareHistoryFileNameFilterChange(fileName: any) {
    this.faceCompareHistoryFileNameSubject.next(fileName);
  }

  private onFaceCompareHistoryFileNameFilterChangeDebounced(fileName: any) {
    const trimmed = (fileName || '').trim();

    if (trimmed === '') {
      this.faceCompareHistoryFilterKey = '';
      this.faceCompareHistoryFilterValue = '';
      this.getFaceCompareHistories();
    } else if (trimmed.length === 1) {
      return;
    } else {
      this.faceCompareHistoryFilterKey = 'image_a_file_name';
      this.faceCompareHistoryFilterValue = trimmed;
      this.getFaceCompareHistories();
    }
  }

  onFaceCompareHistoryNotesFilterChange(notes: any) {
    this.faceCompareHistoryNotesSubject.next(notes);
  }

  private onFaceCompareHistoryNotesFilterChangeDebounced(notes: any) {
    const trimmed = (notes || '').trim();

    if (trimmed === '') {
      this.faceCompareHistoryFilterKey = '';
      this.faceCompareHistoryFilterValue = '';
      this.getFaceCompareHistories();
    } else if (trimmed.length === 1) {
      return;
    } else {
      this.faceCompareHistoryFilterKey = 'notes';
      this.faceCompareHistoryFilterValue = trimmed;
      this.getFaceCompareHistories();
    }
  }

  formatFaceCompareNotes(row: any): string {
    const notes = String(row?.notes || '').trim();
    return notes || 'Face Compare';
  }

  onFaceCompareHistoryStartDateFilterChange(startDate: any) {
    this.faceCompareHistoryStartDateFilter = startDate;

    if (
      this.faceCompareHistoryStartDateFilter === '' &&
      this.faceCompareHistoryEndDateFilter === ''
    ) {
      this.faceCompareHistoryFilterKey = '';
      this.faceCompareHistoryFilterValue = '';
      this.getFaceCompareHistories();
    }

    if (
      this.faceCompareHistoryStartDateFilter &&
      this.faceCompareHistoryEndDateFilter
    ) {
      this.faceCompareHistoryFilterKey = 'created_on';
      this.getFaceCompareHistories();
    }
  }

  onFaceCompareHistoryEndDateFilterChange(endDate: any) {
    this.faceCompareHistoryEndDateFilter = endDate;

    if (
      this.faceCompareHistoryStartDateFilter === '' &&
      this.faceCompareHistoryEndDateFilter === ''
    ) {
      this.faceCompareHistoryFilterKey = '';
      this.faceCompareHistoryFilterValue = '';
      this.getFaceCompareHistories();
    }

    if (
      this.faceCompareHistoryStartDateFilter &&
      this.faceCompareHistoryEndDateFilter
    ) {
      this.faceCompareHistoryFilterKey = 'created_on';
      this.getFaceCompareHistories();
    }
  }

  updateFaceCompareHistoryFilter() {
    this.selectedFaceCompareFilter = 'file-name';
  }

  onFaceCompareCardClick(row: any) {
    // Same reopen path as Face Compare landing history → detector results UI
    if (!row?.face_compare_id) {
      return;
    }
    this.router.navigate(['/osint-image-search/face-compare/detector'], {
      queryParams: { face_compare_id: row.face_compare_id },
      state: {
        isStaleResult: true,
        faceCompareId: row.face_compare_id,
        faceCompareHistory: row,
      },
    });
  }

  closeFaceCompareDetail() {
    this.showFaceCompareDetail = false;
    this.faceCompareDetailHistory = null;
    this.faceCompareDetailResults = [];
    this.faceCompareDetailLoading = false;
  }

  onFaceCompareDetailImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = 'https://placehold.co/400x400?text=Image';
    }
  }

  // VIDEO SCANNER HISTORIES

  videoScannerHistoriesFormatDate(date: any) {
    return this.imageHistoriesFormatDate(date);
  }

  formatVideoScannerStatus(status: any): string {
    if (!status) return '—';
    return String(status).replace(/_/g, ' ');
  }

  onVideoScannerHistoriesPageChange(page: any) {
    this.videoScannerHistoriesSelectAll.isSelected = false;
    this.videoScannerHistoriesCurrentPage = page;
    this.getVideoScannerHistories();
  }

  onVideoScannerHistoryPagedClick(event: Event, row: any) {
    event.stopPropagation();
    const checkbox = event.target as HTMLInputElement;
    this.updateVideoScannerHistoryPaged(row.video_scanner_id, checkbox.checked);
  }

  onVideoScannerHistoriesDeleteButtonClick() {
    this.pendingDeleteAction = 'video-scanner';
    this.showDeleteConfirmation = true;
  }

  updateVideoScannerHistoryPaged(videoScannerId: any, isSelected: any) {
    const selected = this.videoScannerHistoriesPaged.find(
      (row: any) => row.video_scanner_id === videoScannerId,
    );
    if (selected) {
      selected.isSelected = isSelected;
    }
  }

  getVideoScannerHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getVideoScannerHistories(
        this.videoScannerHistoriesCurrentPage,
        this.videoScannerHistoriesPerPage,
        this.videoScannerHistoryFilterKey,
        this.videoScannerHistoryFilterValue,
        this.videoScannerHistoryStartDateFilter,
        this.videoScannerHistoryEndDateFilter,
      )
      .subscribe({
        next: (response) => {
          this.videoScannerHistories = response?.data?.data_list || [];
          this.videoScannerHistoriesPaged = this.addAdditionalKeys([
            ...this.videoScannerHistories,
          ]);
          this.videoScannerHistoriesCount = response?.data?.total_items || 0;
          this.videoScannerHistoriesHasPreviousPage =
            response?.data?.has_previous;
          this.videoScannerHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  deleteVideoScannerHistories() {
    if (!this.selectedVideoScannerHistories?.length) {
      return;
    }

    this.isLoading = true;
    this.searchHistoryService
      .deleteVideoScannerHistories(this.selectedVideoScannerHistories)
      .subscribe({
        next: () => {
          this.videoScannerHistoriesSelectAll.isSelected = false;
          this.updateVideoScannerHistoriesPaged();
          this.updateVideoScannerHistoriesCurrentPage();
          this.getVideoScannerHistories();
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  updateVideoScannerHistoriesPaged() {
    this.videoScannerHistoriesPaged = this.videoScannerHistoriesPaged.filter(
      (row: any) =>
        !this.selectedVideoScannerHistories.includes(row.video_scanner_id),
    );
  }

  updateVideoScannerHistoriesCurrentPage() {
    if (this.videoScannerHistoriesPaged.length === 0) {
      if (!this.videoScannerHistoriesHasNextPage) {
        if (this.videoScannerHistoriesHasPreviousPage) {
          this.videoScannerHistoriesCurrentPage--;
        }
      }
    }
  }

  onVideoScannerHistoriesSelectAllButtonClick() {
    this.videoScannerHistoriesSelectAll.isSelected =
      !this.videoScannerHistoriesSelectAll.isSelected;

    this.videoScannerHistoriesSelectAll.isSelected
      ? this.selectAllVideoScannerHistoriesPaged()
      : this.deselectAllVideoScannerHistoriesPaged();
  }

  selectAllVideoScannerHistoriesPaged() {
    this.videoScannerHistoriesPaged = this.videoScannerHistoriesPaged.map(
      (row: any) => ({
        ...row,
        isSelected: true,
      }),
    );
  }

  deselectAllVideoScannerHistoriesPaged() {
    this.videoScannerHistoriesPaged = this.videoScannerHistoriesPaged.map(
      (row: any) => ({
        ...row,
        isSelected: false,
      }),
    );
  }

  onVideoScannerFilterChange() {
    this.resetVideoScannerHistories();
  }

  resetVideoScannerHistories() {
    this.videoScannerHistoriesCurrentPage = 1;
    this.videoScannerHistoryFilterKey = '';
    this.videoScannerHistoryFilterValue = '';
    this.videoScannerHistoryVideoNameFilter = '';
    this.videoScannerHistoryStartDateFilter = '';
    this.videoScannerHistoryEndDateFilter = '';
  }

  onVideoScannerHistoryVideoNameFilterChange(videoName: any) {
    this.videoScannerHistoryVideoNameSubject.next(videoName);
  }

  private onVideoScannerHistoryVideoNameFilterChangeDebounced(videoName: any) {
    const trimmed = (videoName || '').trim();

    if (trimmed === '') {
      this.videoScannerHistoryFilterKey = '';
      this.videoScannerHistoryFilterValue = '';
      this.getVideoScannerHistories();
    } else if (trimmed.length === 1) {
      return;
    } else {
      this.videoScannerHistoryFilterKey = 'video_name';
      this.videoScannerHistoryFilterValue = trimmed;
      this.getVideoScannerHistories();
    }
  }

  onVideoScannerHistoryStartDateFilterChange(startDate: any) {
    this.videoScannerHistoryStartDateFilter = startDate;

    if (
      this.videoScannerHistoryStartDateFilter === '' &&
      this.videoScannerHistoryEndDateFilter === ''
    ) {
      this.videoScannerHistoryFilterKey = '';
      this.videoScannerHistoryFilterValue = '';
      this.getVideoScannerHistories();
    }

    if (
      this.videoScannerHistoryStartDateFilter &&
      this.videoScannerHistoryEndDateFilter
    ) {
      this.videoScannerHistoryFilterKey = 'created_on';
      this.getVideoScannerHistories();
    }
  }

  onVideoScannerHistoryEndDateFilterChange(endDate: any) {
    this.videoScannerHistoryEndDateFilter = endDate;

    if (
      this.videoScannerHistoryStartDateFilter === '' &&
      this.videoScannerHistoryEndDateFilter === ''
    ) {
      this.videoScannerHistoryFilterKey = '';
      this.videoScannerHistoryFilterValue = '';
      this.getVideoScannerHistories();
    }

    if (
      this.videoScannerHistoryStartDateFilter &&
      this.videoScannerHistoryEndDateFilter
    ) {
      this.videoScannerHistoryFilterKey = 'created_on';
      this.getVideoScannerHistories();
    }
  }

  updateVideoScannerHistoryFilter() {
    this.selectedVideoScannerFilter = 'video-name';
  }

  onVideoScannerCardClick(row: any) {
    // Same reopen path as Video Scanner landing history → scanned-results UI
    if (!row?.video_scanner_id) {
      return;
    }
    this.router.navigate(['/osint-image-search/video-scanner/results'], {
      queryParams: { video_scanner_id: row.video_scanner_id },
      state: {
        isStaleResult: true,
        videoScannerId: row.video_scanner_id,
        videoScannerHistory: row,
      },
    });
  }

  closeVideoScannerDetail() {
    this.showVideoScannerDetail = false;
    this.videoScannerDetailHistory = null;
    this.videoScannerDetailFrames = [];
    this.videoScannerDetailLoading = false;
  }

  onVideoScannerDetailImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = 'https://placehold.co/400x400?text=Frame';
    }
  }

  // OSINT AND LEAK HISTORIES

  onExportExcelButtonClick() {
    switch (this.selectedHistoryTab.type) {
      case 'osint-phone':
        this.exportOsintHistoriesInExcel();
        break;

      case 'osint-email':
        this.exportOsintHistoriesInExcel();
        break;

      case 'osint-username':
        this.exportOsintHistoriesInExcel();
        break;

      case 'osint-name':
        this.exportOsintHistoriesInExcel();
        break;

      case 'osint-wallet':
        this.exportOsintHistoriesInExcel();
        break;

      case 'leak-phone':
        this.exportLeakHistoriesInExcel();
        break;

      case 'leak-email':
        this.exportLeakHistoriesInExcel();
        break;

      case 'leak-username':
        this.exportLeakHistoriesInExcel();
        break;

      case 'leak-password':
        this.exportLeakHistoriesInExcel();
        break;

      case 'vehicle':
        this.exportVehicleHistoriesInExcel();
        break;

      case 'pan':
        this.exportPanHistoriesInExcel();
        break;

      case 'gst':
        this.exportGstHistoriesInExcel();
        break;

      case 'din':
        this.exportDinHistoriesInExcel();
        break;

      case 'mobile-pan':
        this.exportMobilePanHistoriesInExcel();
        break;

      case 'phonepe_fastag':
        this.exportPhonepeFastagHistoriesInExcel();
        break;

      case 'phonepe_upi':
        this.exportPhonepeUpiHistoriesInExcel();
        break;

      case 'virtual-number':
        this.exportVirtualNumberHistoriesInExcel();
        break;

      case 'advanced-search':
        this.exportAdvancedSearchHistoriesInExcel();
        break;

      case 'basic-search':
        this.exportBasicSearchHistoriesInExcel();
        break;

      case 'web-search':
        this.exportWebSearchHistoriesInExcel();
        break;

      default:
        this.exportOsintHistoriesInExcel();
        break;
    }
  }

  onExportCsvButtonClick() {
    switch (this.selectedHistoryTab.type) {
      case 'osint-phone':
        this.exportOsintHistoriesInCsv();
        break;

      case 'osint-email':
        this.exportOsintHistoriesInCsv();
        break;

      case 'osint-username':
        this.exportOsintHistoriesInCsv();
        break;

      case 'osint-name':
        this.exportOsintHistoriesInCsv();
        break;

      case 'osint-wallet':
        this.exportOsintHistoriesInCsv();
        break;

      case 'leak-phone':
        this.exportLeakHistoriesInCsv();
        break;

      case 'leak-email':
        this.exportLeakHistoriesInCsv();
        break;

      case 'leak-username':
        this.exportLeakHistoriesInCsv();
        break;

      case 'leak-password':
        this.exportLeakHistoriesInCsv();
        break;

      case 'vehicle':
        this.exportVehicleHistoriesInCsv();
        break;

      case 'pan':
        this.exportPanHistoriesInCsv();
        break;

      case 'gst':
        this.exportGstHistoriesInCsv();
        break;

      case 'din':
        this.exportDinHistoriesInCsv();
        break;

      case 'mobile-pan':
        this.exportMobilePanHistoriesInCsv();
        break;

      case 'phonepe_fastag':
        this.exportPhonepeFastagHistoriesInCsv();
        break;

      case 'phonepe_upi':
        this.exportPhonepeUpiHistoriesInCsv();
        break;

      case 'virtual-number':
        this.exportVirtualNumberHistoriesInCsv();
        break;

      case 'advanced-search':
        this.exportAdvancedSearchHistoriesInCsv();
        break;

      case 'basic-search':
        this.exportBasicSearchHistoriesInCsv();
        break;

      case 'web-search':
        this.exportWebSearchHistoriesInCsv();
        break;

      default:
        this.exportOsintHistoriesInCsv();
        break;
    }
  }

  onExportPdfButtonClick() {
    switch (this.selectedHistoryTab.type) {
      case 'osint-phone':
        this.exportOsintHistoriesInPdf();
        break;

      case 'osint-email':
        this.exportOsintHistoriesInPdf();
        break;

      case 'osint-username':
        this.exportOsintHistoriesInPdf();
        break;

      case 'osint-name':
        this.exportOsintHistoriesInPdf();
        break;

      case 'osint-wallet':
        this.exportOsintHistoriesInPdf();
        break;

      case 'leak-phone':
        this.exportLeakHistoriesInPdf();
        break;

      case 'leak-email':
        this.exportLeakHistoriesInPdf();
        break;

      case 'leak-username':
        this.exportLeakHistoriesInPdf();
        break;

      case 'leak-password':
        this.exportLeakHistoriesInPdf();
        break;

      case 'vehicle':
        this.exportVehicleHistoriesInPdf();
        break;

      case 'pan':
        this.exportPanHistoriesInPdf();
        break;

      case 'gst':
        this.exportGstHistoriesInPdf();
        break;

      case 'din':
        this.exportDinHistoriesInPdf();
        break;

      case 'mobile-pan':
        this.exportMobilePanHistoriesInPdf();
        break;

      case 'phonepe_fastag':
        this.exportPhonepeFastagHistoriesInPdf();
        break;

      case 'phonepe_upi':
        this.exportPhonepeUpiHistoriesInPdf();
        break;

      case 'virtual-number':
        this.exportVirtualNumberHistoriesInPdf();
        break;

      case 'advanced-search':
        this.exportAdvancedSearchHistoriesInPdf();
        break;

      case 'basic-search':
        this.exportBasicSearchHistoriesInPdf();
        break;

      case 'web-search':
        this.exportWebSearchHistoriesInPdf();
        break;

      default:
        this.exportOsintHistoriesInPdf();
        break;
    }
  }

  onDeleteButtonClick() {
    this.pendingDeleteAction = this.selectedHistoryTab.type;
    this.showDeleteConfirmation = true;
  }

  onConfirmDelete() {
    this.showDeleteConfirmation = false;

    switch (this.pendingDeleteAction) {
      case 'image':
        this.deleteImageHistories();
        break;
      case 'face-compare':
        this.deleteFaceCompareHistories();
        break;
      case 'video-scanner':
        this.deleteVideoScannerHistories();
        break;
      case 'osint-phone':
      case 'osint-email':
      case 'osint-username':
      case 'osint-name':
      case 'osint-wallet':
        this.deleteOsintHistories();
        break;
      case 'leak-phone':
      case 'leak-email':
      case 'leak-username':
      case 'leak-password':
        this.deleteLeakHistories();
        break;
      case 'vehicle':
        this.deleteVehicleHistories();
        break;
      case 'pan':
        this.deletePanHistories();
        break;
      case 'gst':
        this.deleteGstHistories();
        break;

      case 'din':
        this.deleteDinHistories();
        break;
      case 'mobile-pan':
        this.deleteMobilePanHistories();
        break;
      case 'phonepe_fastag':
        this.deletePhonepeFastagHistories();
        break;
      case 'phonepe_upi':
        this.deletePhonepeUpiHistories();
        break;
      case 'virtual-number':
        this.deleteVirtualNumberHistories();
        break;
      case 'advanced-search':
        this.deleteAdvancedSearchHistories();
        break;
      case 'basic-search':
        this.deleteBasicSearchHistories();
        break;
      case 'web-search':
        this.deleteWebSearchHistories();
        break;
      default:
        this.deleteOsintHistories();
        break;
    }

    this.pendingDeleteAction = '';
  }

  onCancelDelete() {
    this.showDeleteConfirmation = false;
    this.pendingDeleteAction = '';
  }

  updateOsintAndLeakHistoryFilters() {
    switch (this.selectedHistoryTab.type) {
      case 'osint-phone':
        this.osintAndLeakFilters = this.osintHistoryFilters.phone;
        break;

      case 'osint-email':
        this.osintAndLeakFilters = this.osintHistoryFilters.email;
        break;

      case 'leak-phone':
        this.osintAndLeakFilters = this.leakHistoryFilters.phone;
        break;
      case 'osint-username':
        this.osintAndLeakFilters = this.osintHistoryFilters.username;
        break;

      case 'osint-name':
        this.osintAndLeakFilters = this.osintHistoryFilters.name;
        break;

      case 'osint-wallet':
        this.osintAndLeakFilters = this.osintHistoryFilters.wallet;
        break;

      case 'leak-email':
        this.osintAndLeakFilters = this.leakHistoryFilters.email;
        break;

      case 'leak-username':
        this.osintAndLeakFilters = this.leakHistoryFilters.username;
        break;

      case 'leak-password':
        this.osintAndLeakFilters = this.leakHistoryFilters.password;
        break;

      case 'vehicle':
        this.osintAndLeakFilters = this.vehicleHistoryFilters;
        break;

      case 'pan':
        this.osintAndLeakFilters = this.panHistoryFilters;
        break;

      case 'gst':
        this.osintAndLeakFilters = this.gstHistoryFilters;
        break;

      case 'din':
        this.osintAndLeakFilters = this.dinHistoryFilters;
        break;

      case 'mobile-pan':
        this.osintAndLeakFilters = this.mobilePanHistoryFilters;
        break;

      case 'phonepe_fastag':
        this.osintAndLeakFilters = this.phonepeFastagHistoryFilters;
        break;

      case 'phonepe_upi':
        this.osintAndLeakFilters = this.phonepeUpiHistoryFilters;
        break;

      case 'virtual-number':
        this.osintAndLeakFilters = this.virtualNumberHistoryFilters;
        break;

      case 'advanced-search':
        this.osintAndLeakFilters = this.advancedSearchHistoryFilters;
        break;

      case 'basic-search':
        this.osintAndLeakFilters = this.basicSearchHistoryFilters;
        break;

      case 'web-search':
        this.osintAndLeakFilters = this.webSearchHistoryFilters;
        break;

      case 'link-analysis':
        this.osintAndLeakFilters = this.linkAnalysisHistoryFilters;
        break;

      default:
        this.osintAndLeakFilters = this.osintHistoryFilters.phone;
        break;
    }

    if (this.osintAndLeakFilters.length > 0) {
      this.selectedOsintAndLeakFilter = this.osintAndLeakFilters[0].value;
    }
  }

  onOsintAndLeakHistoryFilterChange() {
    this.resetOsintHistories();
    this.resetLeakHistories();
    this.resetVehicleHistories();
    this.resetPanHistories();
    this.resetGstHistories();
    this.resetDinHistories();
    this.resetMobilePanHistories();
    this.resetPhonepeFastagHistories();
    this.resetPhonepeUpiHistories();
    this.resetVirtualNumberHistories();
    this.resetAdvancedSearchHistories();
    this.resetBasicSearchHistories();
    this.resetWebSearchHistories();
    this.resetLinkAnalysisHistories();
  }

  // OSINT HISTORY

  getOsintHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getOsintHistories(
        this.selectedOsintHistoryType,
        this.osintHistoriesCurrentPage,
        this.osintHistoriesPerPage,
        this.osintHistoryFilterKey,
        this.osintHistoryFilterValue,
        this.osintHistoryStartDateFilter,
        this.osintHistoryEndDateFilter,
        this.osintHistoriesShouldExport,
        this.osintHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('GET OSINT HISTORIES API RESPONSE: ', response);

          this.osintHistories = response?.data?.data_list;
          this.osintHistoriesPaged = this.addAdditionalKeys([
            ...this.osintHistories,
          ]);
          this.osintHistoriesCount = response?.data?.total_items;
          this.osintHistoriesHasPreviousPage = response?.data?.has_previous;
          this.osintHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportOsintHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportOsintHistories(
        this.selectedOsintHistoryType,
        this.osintHistoriesCurrentPage,
        this.osintHistoriesPerPage,
        this.osintHistoryFilterKey,
        this.osintHistoryFilterValue,
        this.osintHistoryStartDateFilter,
        this.osintHistoryEndDateFilter,
        this.osintHistoriesShouldExport,
        this.osintHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('EXPORT OSINT HISTORIES API RESPONSE: ', response);

          this.osintHistoriesShouldExport = false;
          this.osintHistoriesExportFormat = '';

          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportOsintHistoriesInExcel() {
    this.osintHistoriesShouldExport = true;
    this.osintHistoriesExportFormat = 'xlsx';

    this.exportOsintHistories();
  }

  exportOsintHistoriesInCsv() {
    this.osintHistoriesShouldExport = true;
    this.osintHistoriesExportFormat = 'csv';

    this.exportOsintHistories();
  }

  exportOsintHistoriesInPdf() {
    this.osintHistoriesShouldExport = true;
    this.osintHistoriesExportFormat = 'pdf';

    this.exportOsintHistories();
  }

  saveFile(fileInfo: any) {
    const { fileName, mimeType, base64 } = fileInfo;

    const linkSource = `data:${mimeType};base64,${base64}`;
    const saveLink = document.createElement('a');

    saveLink.href = linkSource;
    saveLink.download = fileName;
    saveLink.click();
  }

  onOsintHistoriesPageChange(page: any) {
    this.osintHistoriesSelectAll.isSelected = false;
    this.osintHistoriesCurrentPage = page;

    this.getOsintHistories();
  }

  onOsintHistoryPagedClick(event: Event, osintHistoryPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateOsintHistoryPaged(isChecked, osintHistoryPaged.id);
  }

  goToOsintHistoryResult(osintHistoryPaged: any) {
    const jobId = osintHistoryPaged?.job_id;
    if (!jobId) {
      return;
    }

    const type = this.selectedOsintHistoryType;
    const routeMap: Record<string, string> = {
      phone: '/osint-number-search/search-result',
      email: '/osint-email-search/search-result',
      username: '/osint-username-search/search-result',
      name: '/osint-name-search/search-result',
      wallet: '/osint-wallet-search/search-result',
    };
    const base = routeMap[type];
    if (!base) {
      return;
    }

    this.router.navigate([`${base}/${jobId}`], {
      state: { searchResult: osintHistoryPaged },
    });
  }

  updateOsintHistoryPaged(isChecked: any, searchId: any) {
    const osintHistoryPagedToUpdate = this.osintHistoriesPaged.find(
      (osintHistoryPaged: any) => osintHistoryPaged.id === searchId,
    );

    if (osintHistoryPagedToUpdate) {
      osintHistoryPagedToUpdate.isSelected = isChecked;
    }
  }

  deleteOsintHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deleteOsintHistories(this.selectedOsintHistories)
      .subscribe({
        next: (response) => {
          // console.log('DELETE OSINT HISTORIES API RESPONSE: ', response);

          this.osintHistoriesSelectAll.isSelected = false;

          this.updateOsintHistoriesPaged();
          this.updateOsintHistoriesCurrentPage();

          this.getOsintHistories();
        },
        error: (error) => {
          // console.error('DELETE OSINT HISTORIES API ERROR: ', error);
          this.isLoading = false;
        },
      });
  }

  updateOsintHistoriesPaged() {
    this.osintHistoriesPaged = this.osintHistoriesPaged.filter(
      (osintHistoryPaged: any) =>
        !this.selectedOsintHistories.includes(
          osintHistoryPaged.osint_search_id,
        ),
    );
  }

  updateOsintHistoriesCurrentPage() {
    if (this.osintHistoriesPaged.length === 0) {
      if (this.osintHistoriesHasNextPage) {
      } else {
        if (this.osintHistoriesHasPreviousPage) {
          this.osintHistoriesCurrentPage--;
        }
      }
    }
  }

  onOsintHistoriesSelectAllClick() {
    this.osintHistoriesSelectAll.isSelected =
      !this.osintHistoriesSelectAll.isSelected;

    this.osintHistoriesSelectAll.isSelected
      ? this.selectAllOsintHistoriesPaged()
      : this.deselectAllOsintHistoriesPaged();
  }

  selectAllOsintHistoriesPaged() {
    this.osintHistoriesPaged = this.osintHistoriesPaged.map(
      (osintHistoryPaged: any) => ({
        ...osintHistoryPaged,
        isSelected: true,
      }),
    );
  }

  deselectAllOsintHistoriesPaged() {
    this.osintHistoriesPaged = this.osintHistoriesPaged.map(
      (osintHistoryPaged: any) => ({
        ...osintHistoryPaged,
        isSelected: false,
      }),
    );
  }

  onOsintHistoryPhoneFilterChange(phone: any) {
    this.osintHistoryPhoneSubject.next(phone);
  }

  private onOsintHistoryPhoneFilterChangeDebounced(phone: any) {
    const trimmedPhone = phone.trim();

    if (trimmedPhone === '') {
      this.osintHistoryFilterKey = '';
      this.osintHistoryFilterValue = '';

      this.getOsintHistories();
    } else if (trimmedPhone.length === 1) {
      return;
    } else {
      this.osintHistoryFilterKey = 'searched_phone';
      this.osintHistoryFilterValue = trimmedPhone;

      this.getOsintHistories();
    }
  }

  onOsintHistoryEmailFilterChange(email: any) {
    this.osintHistoryEmailSubject.next(email);
  }

  private onOsintHistoryEmailFilterChangeDebounced(email: any) {
    const trimmedEmail = email.trim();

    if (trimmedEmail === '') {
      this.osintHistoryFilterKey = '';
      this.osintHistoryFilterValue = '';

      this.getOsintHistories();
    } else if (trimmedEmail.length === 1) {
      return;
    } else {
      this.osintHistoryFilterKey = 'searched_email';
      this.osintHistoryFilterValue = trimmedEmail;

      this.getOsintHistories();
    }
  }

  // ✨ ADDED USERNAME FILTER CHANGE METHOD (similar to phone/email)
  onOsintHistoryUsernameFilterChange(username: any) {
    this.osintHistoryUsernameSubject.next(username);
  }

  private onOsintHistoryUsernameFilterChangeDebounced(username: any) {
    const trimmedUsername = username.trim();

    if (trimmedUsername === '') {
      this.osintHistoryFilterKey = '';
      this.osintHistoryFilterValue = '';

      this.getOsintHistories();
    } else if (trimmedUsername.length === 1) {
      return;
    } else {
      // Assuming the API key for username search is 'searched_username'
      this.osintHistoryFilterKey = 'searched_username';
      this.osintHistoryFilterValue = trimmedUsername;

      this.getOsintHistories();
    }
  }

  onOsintHistoryNameFilterChange(name: any) {
    this.osintHistoryNameSubject.next(name);
  }

  private onOsintHistoryNameFilterChangeDebounced(name: any) {
    const trimmedName = name.trim();

    if (trimmedName === '') {
      this.osintHistoryFilterKey = '';
      this.osintHistoryFilterValue = '';

      this.getOsintHistories();
    } else if (trimmedName.length === 1) {
      return;
    } else {
      this.osintHistoryFilterKey = 'searched_name';
      this.osintHistoryFilterValue = trimmedName;

      this.getOsintHistories();
    }
  }

  onOsintHistoryWalletFilterChange(wallet: any) {
    this.osintHistoryWalletSubject.next(wallet);
  }

  private onOsintHistoryWalletFilterChangeDebounced(wallet: any) {
    const trimmedWallet = wallet.trim();

    if (trimmedWallet === '') {
      this.osintHistoryFilterKey = '';
      this.osintHistoryFilterValue = '';

      this.getOsintHistories();
    } else if (trimmedWallet.length === 1) {
      return;
    } else {
      this.osintHistoryFilterKey = 'searched_wallet';
      this.osintHistoryFilterValue = trimmedWallet;

      this.getOsintHistories();
    }
  }

  onOsintHistoryGstFilterChange(gst: any) {
    this.osintHistoryGstSubject.next(gst);
  }

  private onOsintHistoryGstFilterChangeDebounced(gst: any) {
    const trimmedGst = gst.trim();

    if (trimmedGst === '') {
      this.osintHistoryFilterKey = '';
      this.osintHistoryFilterValue = '';

      this.getOsintHistories();
    } else if (trimmedGst.length === 1) {
      return;
    } else {
      this.osintHistoryFilterKey = 'searched_gst';
      this.osintHistoryFilterValue = trimmedGst;

      this.getOsintHistories();
    }
  }

  onOsintHistoryDinFilterChange(din: any) {
    this.osintHistoryDinSubject.next(din);
  }

  private onOsintHistoryDinFilterChangeDebounced(din: any) {
    const trimmedDin = din.trim();

    if (trimmedDin === '') {
      this.osintHistoryFilterKey = '';
      this.osintHistoryFilterValue = '';

      this.getOsintHistories();
    } else if (trimmedDin.length === 1) {
      return;
    } else {
      this.osintHistoryFilterKey = 'searched_din';
      this.osintHistoryFilterValue = trimmedDin;

      this.getOsintHistories();
    }
  }

  onOsintHistoryStartDateFilterChange(startDate: any) {
    this.osintHistoryStartDateFilter = startDate;

    if (
      this.osintHistoryStartDateFilter === '' &&
      this.osintHistoryEndDateFilter === ''
    ) {
      this.osintHistoryFilterKey = '';
      this.osintHistoryFilterValue = '';

      this.getOsintHistories();
    }

    if (this.osintHistoryStartDateFilter && this.osintHistoryEndDateFilter) {
      this.osintHistoryFilterKey = 'created_on';
      this.getOsintHistories();
    }
  }

  onOsintHistoryEndDateFilterChange(endDate: any) {
    this.osintHistoryEndDateFilter = endDate;

    if (
      this.osintHistoryStartDateFilter === '' &&
      this.osintHistoryEndDateFilter === ''
    ) {
      this.osintHistoryFilterKey = '';
      this.osintHistoryFilterValue = '';

      this.getOsintHistories();
    }

    if (this.osintHistoryStartDateFilter && this.osintHistoryEndDateFilter) {
      this.osintHistoryFilterKey = 'created_on';
      this.getOsintHistories();
    }
  }

  resetOsintHistories() {
    this.osintHistoriesCurrentPage = 1;

    this.osintHistoryFilterKey = '';
    this.osintHistoryFilterValue = '';

    this.osintHistoryIdFilter = '';
    this.osintHistoryPhoneFilter = '';
    this.osintHistoryEmailFilter = '';
    this.osintHistoryUsernameFilter = '';
    this.osintHistoryNameFilter = '';
    this.osintHistoryWalletFilter = '';
    this.osintHistoryGstFilter = '';
    this.osintHistoryDinFilter = '';
    this.osintHistoryStartDateFilter = '';
    this.osintHistoryEndDateFilter = '';
  }

  // LEAK HISTORY

  getLeakHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getLeakHistories(
        this.selectedLeakHistoryType,
        this.leakHistoriesCurrentPage,
        this.leakHistoriesPerPage,
        this.leakHistoryFilterKey,
        this.leakHistoryFilterValue,
        this.leakHistoryStartDateFilter,
        this.leakHistoryEndDateFilter,
        this.leakHistoriesShouldExport,
        this.leakHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('GET LEAK HISTORIES API RESPONSE: ', response);

          this.leakHistories = response?.data?.data_list;
          this.leakHistoriesPaged = this.addAdditionalKeys([
            ...this.leakHistories,
          ]);
          this.leakHistoriesCount = response?.data?.total_items;
          this.leakHistoriesHasPreviousPage = response?.data?.has_previous;
          this.leakHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  deleteLeakHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deleteLeakHistories(this.selectedLeakHistories)
      .subscribe({
        next: (response) => {
          // console.log('DELETE LEAK HISTORIES API RESPONSE: ', response);

          this.leakHistoriesSelectAll.isSelected = false;

          this.updateLeakHistoriesPaged();
          this.updateLeakHistoriesCurrentPage();

          this.getLeakHistories();
        },
        error: (error) => {
          console.error('DELETE LEAK HISTORIES API ERROR: ', error);
          this.isLoading = false;
        },
      });
  }

  onLeakHistoriesPageChange(page: any) {
    this.leakHistoriesSelectAll.isSelected = false;
    this.leakHistoriesCurrentPage = page;

    this.getLeakHistories();
  }

  resetLeakHistories() {
    this.leakHistoriesCurrentPage = 1;

    this.leakHistoryFilterKey = '';
    this.leakHistoryFilterValue = '';

    this.leakHistoryIdFilter = '';
    this.leakHistoryPhoneFilter = '';
    this.leakHistoryEmailFilter = '';
    this.leakHistoryUsernameFilter = '';
    this.leakHistoryPasswordFilter = '';
    this.leakHistoryStartDateFilter = '';
    this.leakHistoryEndDateFilter = '';
  }

  onLeakHistoryPagedClick(event: Event, osintHistoryPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateLeakHistoryPaged(isChecked, osintHistoryPaged.id);
  }

  updateLeakHistoryPaged(isChecked: any, searchId: any) {
    const leakHistoryPagedToUpdate = this.leakHistoriesPaged.find(
      (leakHistoryPaged: any) => leakHistoryPaged.id === searchId,
    );

    if (leakHistoryPagedToUpdate) {
      leakHistoryPagedToUpdate.isSelected = isChecked;
    }
  }

  updateLeakHistoriesPaged() {
    this.leakHistoriesPaged = this.leakHistoriesPaged.filter(
      (leakHistoryPaged: any) =>
        !this.selectedLeakHistories.includes(leakHistoryPaged.leak_search_id),
    );
  }

  updateLeakHistoriesCurrentPage() {
    if (this.leakHistoriesPaged.length === 0) {
      if (this.leakHistoriesHasNextPage) {
      } else {
        if (this.leakHistoriesHasPreviousPage) {
          this.leakHistoriesCurrentPage--;
        }
      }
    }
  }

  onLeakHistoriesSelectAllClick() {
    this.leakHistoriesSelectAll.isSelected =
      !this.leakHistoriesSelectAll.isSelected;

    this.leakHistoriesSelectAll.isSelected
      ? this.selectAllLeakHistoriesPaged()
      : this.deselectAllLeakHistoriesPaged();
  }

  selectAllLeakHistoriesPaged() {
    this.leakHistoriesPaged = this.leakHistoriesPaged.map(
      (leakHistoryPaged: any) => ({
        ...leakHistoryPaged,
        isSelected: true,
      }),
    );
  }

  deselectAllLeakHistoriesPaged() {
    this.leakHistoriesPaged = this.leakHistoriesPaged.map(
      (leakHistoryPaged: any) => ({
        ...leakHistoryPaged,
        isSelected: false,
      }),
    );
  }

  onLeakHistoryPhoneFilterChange(phone: any) {
    this.leakHistoryPhoneSubject.next(phone);
  }

  private onLeakHistoryPhoneFilterChangeDebounced(phone: any) {
    const trimmedPhone = phone.trim();

    if (trimmedPhone === '') {
      this.leakHistoryFilterKey = '';
      this.leakHistoryFilterValue = '';

      this.getLeakHistories();
    } else if (trimmedPhone.length === 1) {
      return;
    } else {
      this.leakHistoryFilterKey = 'query';
      this.leakHistoryFilterValue = trimmedPhone;

      this.getLeakHistories();
    }
  }

  onLeakHistoryEmailFilterChange(email: any) {
    this.leakHistoryEmailSubject.next(email);
  }

  private onLeakHistoryEmailFilterChangeDebounced(email: any) {
    const trimmedEmail = email.trim();

    if (trimmedEmail === '') {
      this.leakHistoryFilterKey = '';
      this.leakHistoryFilterValue = '';

      this.getLeakHistories();
    } else if (trimmedEmail.length === 1) {
      return;
    } else {
      this.leakHistoryFilterKey = 'query';
      this.leakHistoryFilterValue = trimmedEmail;

      this.getLeakHistories();
    }
  }

  onLeakHistoryUsernameFilterChange(username: any) {
    this.leakHistoryUsernameSubject.next(username);
  }

  private onLeakHistoryUsernameFilterChangeDebounced(username: any) {
    const trimmedUsername = username.trim();

    if (trimmedUsername === '') {
      this.leakHistoryFilterKey = '';
      this.leakHistoryFilterValue = '';

      this.getLeakHistories();
    } else if (trimmedUsername.length === 1) {
      return;
    } else {
      this.leakHistoryFilterKey = 'query';
      this.leakHistoryFilterValue = trimmedUsername;

      this.getLeakHistories();
    }
  }

  onLeakHistoryPasswordFilterChange(password: any) {
    this.leakHistoryPasswordSubject.next(password);
  }

  private onLeakHistoryPasswordFilterChangeDebounced(password: any) {
    const trimmedPassword = password.trim();

    if (trimmedPassword === '') {
      this.leakHistoryFilterKey = '';
      this.leakHistoryFilterValue = '';

      this.getLeakHistories();
    } else if (trimmedPassword.length === 1) {
      return;
    } else {
      this.leakHistoryFilterKey = 'query';
      this.leakHistoryFilterValue = trimmedPassword;

      this.getLeakHistories();
    }
  }

  onLeakHistoryStartDateFilterChange(startDate: any) {
    this.leakHistoryStartDateFilter = startDate;

    if (
      this.leakHistoryStartDateFilter === '' &&
      this.leakHistoryEndDateFilter === ''
    ) {
      this.leakHistoryFilterKey = '';
      this.leakHistoryFilterValue = '';

      this.getLeakHistories();
    }

    if (this.leakHistoryStartDateFilter && this.leakHistoryEndDateFilter) {
      this.leakHistoryFilterKey = 'created_on';
      this.getLeakHistories();
    }
  }

  onLeakHistoryEndDateFilterChange(endDate: any) {
    this.leakHistoryEndDateFilter = endDate;

    if (
      this.leakHistoryStartDateFilter === '' &&
      this.leakHistoryEndDateFilter === ''
    ) {
      this.leakHistoryFilterKey = '';
      this.leakHistoryFilterValue = '';

      this.getLeakHistories();
    }

    if (this.leakHistoryStartDateFilter && this.leakHistoryEndDateFilter) {
      this.leakHistoryFilterKey = 'created_on';
      this.getLeakHistories();
    }
  }

  exportLeakHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportLeakHistories(
        this.selectedLeakHistoryType,
        this.leakHistoriesCurrentPage,
        this.leakHistoriesPerPage,
        this.leakHistoryFilterKey,
        this.leakHistoryFilterValue,
        this.leakHistoryStartDateFilter,
        this.leakHistoryEndDateFilter,
        this.leakHistoriesShouldExport,
        this.leakHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('EXPORT LEAK HISTORIES API RESPONSE: ', response);

          this.leakHistoriesShouldExport = false;
          this.leakHistoriesExportFormat = '';

          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportLeakHistoriesInExcel() {
    this.leakHistoriesShouldExport = true;
    this.leakHistoriesExportFormat = 'xlsx';

    this.exportLeakHistories();
  }

  exportLeakHistoriesInCsv() {
    this.leakHistoriesShouldExport = true;
    this.leakHistoriesExportFormat = 'csv';

    this.exportLeakHistories();
  }

  exportLeakHistoriesInPdf() {
    this.leakHistoriesShouldExport = true;
    this.leakHistoriesExportFormat = 'pdf';

    this.exportLeakHistories();
  }

  // VEHICLE HISTORY

  getVehicleHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getVehicleHistories(
        this.vehicleHistoriesCurrentPage,
        this.vehicleHistoriesPerPage,
        this.vehicleHistoryFilterKey,
        this.vehicleHistoryFilterValue,
        this.vehicleHistoryStartDateFilter,
        this.vehicleHistoryEndDateFilter,
        this.vehicleHistoriesShouldExport,
        this.vehicleHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('GET VEHICLE HISTORIES API RESPONSE: ', response);

          this.vehicleHistories = response?.data?.data_list;
          this.vehicleHistoriesPaged = this.addAdditionalKeys([
            ...this.vehicleHistories,
          ]);
          this.vehicleHistoriesCount = response?.data?.total_items;
          this.vehicleHistoriesHasPreviousPage = response?.data?.has_previous;
          this.vehicleHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportVehicleHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportVehicleHistories(
        this.vehicleHistoriesCurrentPage,
        this.vehicleHistoriesPerPage,
        this.vehicleHistoryFilterKey,
        this.vehicleHistoryFilterValue,
        this.vehicleHistoryStartDateFilter,
        this.vehicleHistoryEndDateFilter,
        this.vehicleHistoriesShouldExport,
        this.vehicleHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('EXPORT VEHICLE HISTORIES API RESPONSE: ', response);

          this.vehicleHistoriesShouldExport = false;
          this.vehicleHistoriesExportFormat = '';

          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportVehicleHistoriesInExcel() {
    this.vehicleHistoriesShouldExport = true;
    this.vehicleHistoriesExportFormat = 'xlsx';

    this.exportVehicleHistories();
  }

  exportVehicleHistoriesInCsv() {
    this.vehicleHistoriesShouldExport = true;
    this.vehicleHistoriesExportFormat = 'csv';

    this.exportVehicleHistories();
  }

  exportVehicleHistoriesInPdf() {
    this.vehicleHistoriesShouldExport = true;
    this.vehicleHistoriesExportFormat = 'pdf';

    this.exportVehicleHistories();
  }

  onVehicleHistoriesPageChange(page: any) {
    this.vehicleHistoriesSelectAll.isSelected = false;
    this.vehicleHistoriesCurrentPage = page;

    this.getVehicleHistories();
  }

  onVehicleHistoryPagedClick(event: Event, vehicleHistoryPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateVehicleHistoryPaged(isChecked, vehicleHistoryPaged.id);
  }

  updateVehicleHistoryPaged(isChecked: any, searchId: any) {
    const vehicleHistoryPagedToUpdate = this.vehicleHistoriesPaged.find(
      (vehicleHistoryPaged: any) => vehicleHistoryPaged.id === searchId,
    );

    if (vehicleHistoryPagedToUpdate) {
      vehicleHistoryPagedToUpdate.isSelected = isChecked;
    }
  }

  deleteVehicleHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deleteVehicleHistories(this.selectedVehicleHistories)
      .subscribe({
        next: (response) => {
          // console.log('DELETE VEHICLE HISTORIES API RESPONSE: ', response);

          this.vehicleHistoriesSelectAll.isSelected = false;

          this.updateVehicleHistoriesPaged();
          this.updateVehicleHistoriesCurrentPage();

          this.getVehicleHistories();
        },
        error: (error) => {
          console.error('DELETE VEHICLE HISTORIES API ERROR: ', error);
          this.isLoading = false;
        },
      });
  }

  updateVehicleHistoriesPaged() {
    this.vehicleHistoriesPaged = this.vehicleHistoriesPaged.filter(
      (vehicleHistoryPaged: any) =>
        !this.selectedVehicleHistories.includes(
          vehicleHistoryPaged.vehicle_search_id,
        ),
    );
  }

  updateVehicleHistoriesCurrentPage() {
    if (this.vehicleHistoriesPaged.length === 0) {
      if (this.vehicleHistoriesHasNextPage) {
      } else {
        if (this.vehicleHistoriesHasPreviousPage) {
          this.vehicleHistoriesCurrentPage--;
        }
      }
    }
  }

  onVehicleHistoriesSelectAllClick() {
    this.vehicleHistoriesSelectAll.isSelected =
      !this.vehicleHistoriesSelectAll.isSelected;

    this.vehicleHistoriesSelectAll.isSelected
      ? this.selectAllVehicleHistoriesPaged()
      : this.deselectAllVehicleHistoriesPaged();
  }

  selectAllVehicleHistoriesPaged() {
    this.vehicleHistoriesPaged = this.vehicleHistoriesPaged.map(
      (vehicleHistoryPaged: any) => ({
        ...vehicleHistoryPaged,
        isSelected: true,
      }),
    );
  }

  deselectAllVehicleHistoriesPaged() {
    this.vehicleHistoriesPaged = this.vehicleHistoriesPaged.map(
      (vehicleHistoryPaged: any) => ({
        ...vehicleHistoryPaged,
        isSelected: false,
      }),
    );
  }

  onVehicleHistoryIdFilterChange(id: any) {
    this.vehicleHistoryIdSubject.next(id);
  }

  private onVehicleHistoryIdFilterChangeDebounced(id: any) {
    const trimmedId = id.trim();

    if (trimmedId === '') {
      this.vehicleHistoryFilterKey = '';
      this.vehicleHistoryFilterValue = '';

      this.getVehicleHistories();
    } else if (trimmedId.length === 1) {
      return;
    } else {
      this.vehicleHistoryFilterKey = 'id';
      this.vehicleHistoryFilterValue = trimmedId;

      this.getVehicleHistories();
    }
  }

  onVehicleHistoryVehicleNumberFilterChange(vehicleNumber: any) {
    this.vehicleHistoryVehicleNumberSubject.next(vehicleNumber);
  }

  private onVehicleHistoryVehicleNumberFilterChangeDebounced(vehicleNumber: any) {
    const trimmedVehicleNumber = vehicleNumber.trim();

    if (trimmedVehicleNumber === '') {
      this.vehicleHistoryFilterKey = '';
      this.vehicleHistoryFilterValue = '';

      this.getVehicleHistories();
    } else if (trimmedVehicleNumber.length === 1) {
      return;
    } else {
      this.vehicleHistoryFilterKey = 'vehicle_number';
      this.vehicleHistoryFilterValue = trimmedVehicleNumber;

      this.getVehicleHistories();
    }
  }

  onVehicleHistoryStartDateFilterChange(startDate: any) {
    this.vehicleHistoryStartDateFilter = startDate;

    if (
      this.vehicleHistoryStartDateFilter === '' &&
      this.vehicleHistoryEndDateFilter === ''
    ) {
      this.vehicleHistoryFilterKey = '';
      this.vehicleHistoryFilterValue = '';

      this.getVehicleHistories();
    }

    if (
      this.vehicleHistoryStartDateFilter &&
      this.vehicleHistoryEndDateFilter
    ) {
      this.vehicleHistoryFilterKey = 'created_on';
      this.getVehicleHistories();
    }
  }

  onVehicleHistoryEndDateFilterChange(endDate: any) {
    this.vehicleHistoryEndDateFilter = endDate;

    if (
      this.vehicleHistoryStartDateFilter === '' &&
      this.vehicleHistoryEndDateFilter === ''
    ) {
      this.vehicleHistoryFilterKey = '';
      this.vehicleHistoryFilterValue = '';

      this.getVehicleHistories();
    }

    if (
      this.vehicleHistoryStartDateFilter &&
      this.vehicleHistoryEndDateFilter
    ) {
      this.vehicleHistoryFilterKey = 'created_on';
      this.getVehicleHistories();
    }
  }

  resetVehicleHistories() {
    this.vehicleHistoriesCurrentPage = 1;

    this.vehicleHistoryFilterKey = '';
    this.vehicleHistoryFilterValue = '';

    this.vehicleHistoryIdFilter = '';
    this.vehicleHistoryVehicleNumberFilter = '';
    this.vehicleHistoryStartDateFilter = '';
    this.vehicleHistoryEndDateFilter = '';
  }

  // PAN HISTORY

  getPanHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getPanHistories(
        this.panHistoriesCurrentPage,
        this.panHistoriesPerPage,
        this.panHistoryFilterKey,
        this.panHistoryFilterValue,
        this.panHistoryStartDateFilter,
        this.panHistoryEndDateFilter,
        this.panHistoriesShouldExport,
        this.panHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('GET PAN HISTORIES API RESPONSE: ', response);

          this.panHistories = response?.data?.data_list;
          this.panHistoriesPaged = this.addAdditionalKeys([
            ...this.panHistories,
          ]);
          this.panHistoriesCount = response?.data?.total_items;
          this.panHistoriesHasPreviousPage = response?.data?.has_previous;
          this.panHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportPanHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportPanHistories(
        this.panHistoriesCurrentPage,
        this.panHistoriesPerPage,
        this.panHistoryFilterKey,
        this.panHistoryFilterValue,
        this.panHistoryStartDateFilter,
        this.panHistoryEndDateFilter,
        this.panHistoriesShouldExport,
        this.panHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('EXPORT PAN HISTORIES API RESPONSE: ', response);

          this.panHistoriesShouldExport = false;
          this.panHistoriesExportFormat = '';

          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportPanHistoriesInExcel() {
    this.panHistoriesShouldExport = true;
    this.panHistoriesExportFormat = 'xlsx';

    this.exportPanHistories();
  }

  exportPanHistoriesInCsv() {
    this.panHistoriesShouldExport = true;
    this.panHistoriesExportFormat = 'csv';

    this.exportPanHistories();
  }

  exportPanHistoriesInPdf() {
    this.panHistoriesShouldExport = true;
    this.panHistoriesExportFormat = 'pdf';

    this.exportPanHistories();
  }

  onPanHistoriesPageChange(page: any) {
    this.panHistoriesSelectAll.isSelected = false;
    this.gstHistoriesSelectAll.isSelected = false;
    this.dinHistoriesSelectAll.isSelected = false;
    this.panHistoriesCurrentPage = page;

    this.getPanHistories();
  }

  onPanHistoryPagedClick(event: Event, panHistoryPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updatePanHistoryPaged(isChecked, panHistoryPaged.id);
  }

  updatePanHistoryPaged(isChecked: any, searchId: any) {
    const panHistoryPagedToUpdate = this.panHistoriesPaged.find(
      (panHistoryPaged: any) => panHistoryPaged.id === searchId,
    );

    if (panHistoryPagedToUpdate) {
      panHistoryPagedToUpdate.isSelected = isChecked;
    }
  }

  deletePanHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deletePanHistories(this.selectedPanHistories)
      .subscribe({
        next: (response) => {
          // console.log('DELETE PAN HISTORIES API RESPONSE: ', response);

          this.panHistoriesSelectAll.isSelected = false;
    this.gstHistoriesSelectAll.isSelected = false;
    this.dinHistoriesSelectAll.isSelected = false;

          this.updatePanHistoriesPaged();
          this.updatePanHistoriesCurrentPage();

          this.getPanHistories();
        },
        error: (error) => {
          console.error('DELETE PAN HISTORIES API ERROR: ', error);
          this.isLoading = false;
        },
      });
  }

  updatePanHistoriesPaged() {
    this.panHistoriesPaged = this.panHistoriesPaged.filter(
      (panHistoryPaged: any) =>
        !this.selectedPanHistories.includes(panHistoryPaged.pan_search_id),
    );
  }

  updatePanHistoriesCurrentPage() {
    if (this.panHistoriesPaged.length === 0) {
      if (this.panHistoriesHasNextPage) {
      } else {
        if (this.panHistoriesHasPreviousPage) {
          this.panHistoriesCurrentPage--;
        }
      }
    }
  }

  onPanHistoriesSelectAllClick() {
    this.panHistoriesSelectAll.isSelected =
      !this.panHistoriesSelectAll.isSelected;

    this.panHistoriesSelectAll.isSelected
      ? this.selectAllPanHistoriesPaged()
      : this.deselectAllPanHistoriesPaged();
  }

  selectAllPanHistoriesPaged() {
    this.panHistoriesPaged = this.panHistoriesPaged.map(
      (panHistoryPaged: any) => ({
        ...panHistoryPaged,
        isSelected: true,
      }),
    );
  }

  deselectAllPanHistoriesPaged() {
    this.panHistoriesPaged = this.panHistoriesPaged.map(
      (panHistoryPaged: any) => ({
        ...panHistoryPaged,
        isSelected: false,
      }),
    );
  }

  onPanHistoryIdFilterChange(id: any) {
    this.panHistoryIdSubject.next(id);
  }

  private onPanHistoryIdFilterChangeDebounced(id: any) {
    const trimmedId = id.trim();

    if (trimmedId === '') {
      this.panHistoryFilterKey = '';
      this.panHistoryFilterValue = '';

      this.getPanHistories();
    } else if (trimmedId.length === 1) {
      return;
    } else {
      this.panHistoryFilterKey = 'id';
      this.panHistoryFilterValue = trimmedId;

      this.getPanHistories();
    }
  }

  onPanHistoryPanNumberFilterChange(panNumber: any) {
    this.panHistoryPanNumberSubject.next(panNumber);
  }

  private onPanHistoryPanNumberFilterChangeDebounced(panNumber: any) {
    const trimmedPanNumber = panNumber.trim();

    if (trimmedPanNumber === '') {
      this.panHistoryFilterKey = '';
      this.panHistoryFilterValue = '';

      this.getPanHistories();
    } else if (trimmedPanNumber.length === 1) {
      return;
    } else {
      this.panHistoryFilterKey = 'pan_number';
      this.panHistoryFilterValue = trimmedPanNumber;

      this.getPanHistories();
    }
  }

  onPanHistoryStartDateFilterChange(startDate: any) {
    this.panHistoryStartDateFilter = startDate;

    if (
      this.panHistoryStartDateFilter === '' &&
      this.panHistoryEndDateFilter === ''
    ) {
      this.panHistoryFilterKey = '';
      this.panHistoryFilterValue = '';

      this.getPanHistories();
    }

    if (this.panHistoryStartDateFilter && this.panHistoryEndDateFilter) {
      this.panHistoryFilterKey = 'created_on';
      this.getPanHistories();
    }
  }

  onPanHistoryEndDateFilterChange(endDate: any) {
    this.panHistoryEndDateFilter = endDate;

    if (
      this.panHistoryStartDateFilter === '' &&
      this.panHistoryEndDateFilter === ''
    ) {
      this.panHistoryFilterKey = '';
      this.panHistoryFilterValue = '';

      this.getPanHistories();
    }

    if (this.panHistoryStartDateFilter && this.panHistoryEndDateFilter) {
      this.panHistoryFilterKey = 'created_on';
      this.getPanHistories();
    }
  }

  resetPanHistories() {
    this.panHistoriesCurrentPage = 1;

    this.panHistoryFilterKey = '';
    this.panHistoryFilterValue = '';

    this.panHistoryIdFilter = '';
    this.panHistoryPanNumberFilter = '';
    this.panHistoryStartDateFilter = '';
    this.panHistoryEndDateFilter = '';
  }


  // GST HISTORY

  getGstHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getGstHistories(
        this.gstHistoriesCurrentPage,
        this.gstHistoriesPerPage,
        this.gstHistoryFilterKey,
        this.gstHistoryFilterValue,
        this.gstHistoryStartDateFilter,
        this.gstHistoryEndDateFilter,
        this.gstHistoriesShouldExport,
        this.gstHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.gstHistories = response?.data?.data_list;
          this.gstHistoriesPaged = this.addAdditionalKeys([
            ...this.gstHistories,
          ]);
          this.gstHistoriesCount = response?.data?.total_items;
          this.gstHistoriesHasPreviousPage = response?.data?.has_previous;
          this.gstHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportGstHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportGstHistories(
        this.gstHistoriesCurrentPage,
        this.gstHistoriesPerPage,
        this.gstHistoryFilterKey,
        this.gstHistoryFilterValue,
        this.gstHistoryStartDateFilter,
        this.gstHistoryEndDateFilter,
        this.gstHistoriesShouldExport,
        this.gstHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.gstHistoriesShouldExport = false;
          this.gstHistoriesExportFormat = '';
          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportGstHistoriesInExcel() {
    this.gstHistoriesShouldExport = true;
    this.gstHistoriesExportFormat = 'xlsx';
    this.exportGstHistories();
  }

  exportGstHistoriesInCsv() {
    this.gstHistoriesShouldExport = true;
    this.gstHistoriesExportFormat = 'csv';
    this.exportGstHistories();
  }

  exportGstHistoriesInPdf() {
    this.gstHistoriesShouldExport = true;
    this.gstHistoriesExportFormat = 'pdf';
    this.exportGstHistories();
  }

  onGstHistoriesPageChange(page: any) {
    this.gstHistoriesSelectAll.isSelected = false;
    this.dinHistoriesSelectAll.isSelected = false;
    this.gstHistoriesCurrentPage = page;
    this.getGstHistories();
  }

  onGstHistoryPagedClick(event: Event, gstHistoryPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    this.updateGstHistoryPaged(isChecked, gstHistoryPaged.id);
  }

  updateGstHistoryPaged(isChecked: any, searchId: any) {
    const gstHistoryPagedToUpdate = this.gstHistoriesPaged.find(
      (gstHistoryPaged: any) => gstHistoryPaged.id === searchId,
    );
    if (gstHistoryPagedToUpdate) {
      gstHistoryPagedToUpdate.isSelected = isChecked;
    }
  }

  deleteGstHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deleteGstHistories(this.selectedGstHistories)
      .subscribe({
        next: () => {
          this.gstHistoriesSelectAll.isSelected = false;
    this.dinHistoriesSelectAll.isSelected = false;
          this.updateGstHistoriesPaged();
          this.updateGstHistoriesCurrentPage();
          this.getGstHistories();
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  updateGstHistoriesPaged() {
    this.gstHistoriesPaged = this.gstHistoriesPaged.filter(
      (gstHistoryPaged: any) =>
        !this.selectedGstHistories.includes(gstHistoryPaged.gst_search_id),
    );
  }

  updateGstHistoriesCurrentPage() {
    if (this.gstHistoriesPaged.length === 0) {
      if (this.gstHistoriesHasNextPage) {
      } else if (this.gstHistoriesHasPreviousPage) {
        this.gstHistoriesCurrentPage--;
      }
    }
  }

  onGstHistoriesSelectAllClick() {
    this.gstHistoriesSelectAll.isSelected =
      !this.gstHistoriesSelectAll.isSelected;
    this.gstHistoriesSelectAll.isSelected
      ? this.gstHistoriesPagedSelectAll()
      : this.gstHistoriesPagedDeselectAll();
  }

  gstHistoriesPagedSelectAll() {
    this.gstHistoriesPaged = this.gstHistoriesPaged.map(
      (gstHistoryPaged: any) => ({
        ...gstHistoryPaged,
        isSelected: true,
      }),
    );
  }

  gstHistoriesPagedDeselectAll() {
    this.gstHistoriesPaged = this.gstHistoriesPaged.map(
      (gstHistoryPaged: any) => ({
        ...gstHistoryPaged,
        isSelected: false,
      }),
    );
  }

  goToGstHistoryResult(gstHistoryPaged: any) {
    const jobId = gstHistoryPaged?.job_id;
    if (!jobId) {
      return;
    }
    this.router.navigate([`/gst-search-result/${jobId}`], {
      state: { searchResult: gstHistoryPaged },
    });
  }

  onGstHistoryIdFilterChange(id: any) {
    const trimmedId = String(id || '').trim();
    if (trimmedId === '') {
      this.gstHistoryFilterKey = '';
      this.gstHistoryFilterValue = '';
      this.getGstHistories();
    } else if (trimmedId.length === 1) {
      return;
    } else {
      this.gstHistoryFilterKey = 'gst_search_id';
      this.gstHistoryFilterValue = trimmedId;
      this.getGstHistories();
    }
  }

  onGstHistoryGstFilterChange(gst: any) {
    const trimmedGst = String(gst || '').trim();
    if (trimmedGst === '') {
      this.gstHistoryFilterKey = '';
      this.gstHistoryFilterValue = '';
      this.getGstHistories();
    } else if (trimmedGst.length === 1) {
      return;
    } else {
      this.gstHistoryFilterKey = 'searched_gst';
      this.gstHistoryFilterValue = trimmedGst;
      this.getGstHistories();
    }
  }

  onGstHistoryStartDateFilterChange(startDate: any) {
    this.gstHistoryStartDateFilter = startDate;
    if (this.gstHistoryStartDateFilter === '' && this.gstHistoryEndDateFilter === '') {
      this.gstHistoryFilterKey = '';
      this.gstHistoryFilterValue = '';
      this.getGstHistories();
    }
    if (this.gstHistoryStartDateFilter && this.gstHistoryEndDateFilter) {
      this.gstHistoryFilterKey = 'created_on';
      this.getGstHistories();
    }
  }

  onGstHistoryEndDateFilterChange(endDate: any) {
    this.gstHistoryEndDateFilter = endDate;
    if (this.gstHistoryStartDateFilter === '' && this.gstHistoryEndDateFilter === '') {
      this.gstHistoryFilterKey = '';
      this.gstHistoryFilterValue = '';
      this.getGstHistories();
    }
    if (this.gstHistoryStartDateFilter && this.gstHistoryEndDateFilter) {
      this.gstHistoryFilterKey = 'created_on';
      this.getGstHistories();
    }
  }

  resetGstHistories() {
    this.gstHistoriesCurrentPage = 1;
    this.gstHistoryFilterKey = '';
    this.gstHistoryFilterValue = '';
    this.gstHistoryIdFilter = '';
    this.gstHistoryGstFilter = '';
    this.gstHistoryStartDateFilter = '';
    this.gstHistoryEndDateFilter = '';
  }

  // DIN HISTORY

  getDinHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getDinHistories(
        this.dinHistoriesCurrentPage,
        this.dinHistoriesPerPage,
        this.dinHistoryFilterKey,
        this.dinHistoryFilterValue,
        this.dinHistoryStartDateFilter,
        this.dinHistoryEndDateFilter,
        this.dinHistoriesShouldExport,
        this.dinHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.dinHistories = response?.data?.data_list;
          this.dinHistoriesPaged = this.addAdditionalKeys([
            ...this.dinHistories,
          ]);
          this.dinHistoriesCount = response?.data?.total_items;
          this.dinHistoriesHasPreviousPage = response?.data?.has_previous;
          this.dinHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportDinHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportDinHistories(
        this.dinHistoriesCurrentPage,
        this.dinHistoriesPerPage,
        this.dinHistoryFilterKey,
        this.dinHistoryFilterValue,
        this.dinHistoryStartDateFilter,
        this.dinHistoryEndDateFilter,
        this.dinHistoriesShouldExport,
        this.dinHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.dinHistoriesShouldExport = false;
          this.dinHistoriesExportFormat = '';
          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportDinHistoriesInExcel() {
    this.dinHistoriesShouldExport = true;
    this.dinHistoriesExportFormat = 'xlsx';
    this.exportDinHistories();
  }

  exportDinHistoriesInCsv() {
    this.dinHistoriesShouldExport = true;
    this.dinHistoriesExportFormat = 'csv';
    this.exportDinHistories();
  }

  exportDinHistoriesInPdf() {
    this.dinHistoriesShouldExport = true;
    this.dinHistoriesExportFormat = 'pdf';
    this.exportDinHistories();
  }

  onDinHistoriesPageChange(page: any) {
    this.dinHistoriesSelectAll.isSelected = false;
    this.dinHistoriesCurrentPage = page;
    this.getDinHistories();
  }

  onDinHistoryPagedClick(event: Event, dinHistoryPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    this.updateDinHistoryPaged(isChecked, dinHistoryPaged.id);
  }

  updateDinHistoryPaged(isChecked: any, searchId: any) {
    const dinHistoryPagedToUpdate = this.dinHistoriesPaged.find(
      (dinHistoryPaged: any) => dinHistoryPaged.id === searchId,
    );
    if (dinHistoryPagedToUpdate) {
      dinHistoryPagedToUpdate.isSelected = isChecked;
    }
  }

  deleteDinHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deleteDinHistories(this.selectedDinHistories)
      .subscribe({
        next: () => {
          this.dinHistoriesSelectAll.isSelected = false;
          this.updateDinHistoriesPaged();
          this.updateDinHistoriesCurrentPage();
          this.getDinHistories();
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  updateDinHistoriesPaged() {
    this.dinHistoriesPaged = this.dinHistoriesPaged.filter(
      (dinHistoryPaged: any) =>
        !this.selectedDinHistories.includes(dinHistoryPaged.din_search_id),
    );
  }

  updateDinHistoriesCurrentPage() {
    if (this.dinHistoriesPaged.length === 0) {
      if (this.dinHistoriesHasNextPage) {
      } else if (this.dinHistoriesHasPreviousPage) {
        this.dinHistoriesCurrentPage--;
      }
    }
  }

  onDinHistoriesSelectAllClick() {
    this.dinHistoriesSelectAll.isSelected =
      !this.dinHistoriesSelectAll.isSelected;
    this.dinHistoriesSelectAll.isSelected
      ? this.dinHistoriesPagedSelectAll()
      : this.dinHistoriesPagedDeselectAll();
  }

  dinHistoriesPagedSelectAll() {
    this.dinHistoriesPaged = this.dinHistoriesPaged.map(
      (dinHistoryPaged: any) => ({
        ...dinHistoryPaged,
        isSelected: true,
      }),
    );
  }

  dinHistoriesPagedDeselectAll() {
    this.dinHistoriesPaged = this.dinHistoriesPaged.map(
      (dinHistoryPaged: any) => ({
        ...dinHistoryPaged,
        isSelected: false,
      }),
    );
  }

  goToDinHistoryResult(dinHistoryPaged: any) {
    const jobId = dinHistoryPaged?.job_id;
    if (!jobId) {
      return;
    }
    this.router.navigate([`/din-search-result/${jobId}`], {
      state: { searchResult: dinHistoryPaged },
    });
  }

  onDinHistoryIdFilterChange(id: any) {
    const trimmedId = String(id || '').trim();
    if (trimmedId === '') {
      this.dinHistoryFilterKey = '';
      this.dinHistoryFilterValue = '';
      this.getDinHistories();
    } else if (trimmedId.length === 1) {
      return;
    } else {
      this.dinHistoryFilterKey = 'din_search_id';
      this.dinHistoryFilterValue = trimmedId;
      this.getDinHistories();
    }
  }

  onDinHistoryDinFilterChange(din: any) {
    const trimmedDin = String(din || '').trim();
    if (trimmedDin === '') {
      this.dinHistoryFilterKey = '';
      this.dinHistoryFilterValue = '';
      this.getDinHistories();
    } else if (trimmedDin.length === 1) {
      return;
    } else {
      this.dinHistoryFilterKey = 'searched_din';
      this.dinHistoryFilterValue = trimmedDin;
      this.getDinHistories();
    }
  }

  onDinHistoryStartDateFilterChange(startDate: any) {
    this.dinHistoryStartDateFilter = startDate;
    if (this.dinHistoryStartDateFilter === '' && this.dinHistoryEndDateFilter === '') {
      this.dinHistoryFilterKey = '';
      this.dinHistoryFilterValue = '';
      this.getDinHistories();
    }
    if (this.dinHistoryStartDateFilter && this.dinHistoryEndDateFilter) {
      this.dinHistoryFilterKey = 'created_on';
      this.getDinHistories();
    }
  }

  onDinHistoryEndDateFilterChange(endDate: any) {
    this.dinHistoryEndDateFilter = endDate;
    if (this.dinHistoryStartDateFilter === '' && this.dinHistoryEndDateFilter === '') {
      this.dinHistoryFilterKey = '';
      this.dinHistoryFilterValue = '';
      this.getDinHistories();
    }
    if (this.dinHistoryStartDateFilter && this.dinHistoryEndDateFilter) {
      this.dinHistoryFilterKey = 'created_on';
      this.getDinHistories();
    }
  }

  resetDinHistories() {
    this.dinHistoriesCurrentPage = 1;
    this.dinHistoryFilterKey = '';
    this.dinHistoryFilterValue = '';
    this.dinHistoryIdFilter = '';
    this.dinHistoryDinFilter = '';
    this.dinHistoryStartDateFilter = '';
    this.dinHistoryEndDateFilter = '';
  }


  // MOBILE PAN HISTORY

  mobilePanHistories: any = [];
  mobilePanHistoriesPaged: any = [];

  mobilePanHistoriesCount: any;
  mobilePanHistoriesPerPage = 10;
  mobilePanHistoriesCurrentPage = 1;
  mobilePanHistoriesHasPreviousPage: any;
  mobilePanHistoriesHasNextPage: any;

  mobilePanHistoriesSelectAll = { isSelected: false };

  mobilePanHistoryFilters: any = [
    { label: 'Id', value: 'id' },
    { label: 'Phone', value: 'phone' },
    { label: 'Date Added', value: 'date-added' },
  ];

  mobilePanHistoryFilterKey: any = '';
  mobilePanHistoryFilterValue: any = '';

  mobilePanHistoryIdFilter: any = '';
  mobilePanHistoryPhoneFilter: any = '';
  mobilePanHistoryStartDateFilter: any = '';
  mobilePanHistoryEndDateFilter: any = '';

  mobilePanHistoriesShouldExport: any;
  mobilePanHistoriesExportFormat: any;

  mobilePanHistoryTableThs: any = ['Id', 'Phone', 'Date Added', 'Time'];

  mobilePanHistoryTableTds: any = [
    { field: 'id' },
    { field: 'searched_phone' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get mobilePanHistoriesPagedStartAndEnd() {
    if (!this.mobilePanHistories?.length || !this.mobilePanHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.mobilePanHistoriesCurrentPage - 1) * this.mobilePanHistoriesPerPage + 1;
    const end = Math.min(
      start + this.mobilePanHistories.length - 1,
      this.mobilePanHistoriesCount,
    );

    return { start, end };
  }

  get selectedMobilePanHistories() {
    return this.mobilePanHistoriesPaged
      .filter((mobilePanHistoryPaged: any) => mobilePanHistoryPaged.isSelected)
      .map((mobilePanHistoryPaged: any) => mobilePanHistoryPaged.mobile_pan_search_id);
  }

  getMobilePanHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getMobilePanHistories(
        this.mobilePanHistoriesCurrentPage,
        this.mobilePanHistoriesPerPage,
        this.mobilePanHistoryFilterKey,
        this.mobilePanHistoryFilterValue,
        this.mobilePanHistoryStartDateFilter,
        this.mobilePanHistoryEndDateFilter,
        this.mobilePanHistoriesShouldExport,
        this.mobilePanHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.mobilePanHistories = response?.data?.data_list;
          this.mobilePanHistoriesPaged = this.addAdditionalKeys([
            ...this.mobilePanHistories,
          ]);
          this.mobilePanHistoriesCount = response?.data?.total_items;
          this.mobilePanHistoriesHasPreviousPage = response?.data?.has_previous;
          this.mobilePanHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportMobilePanHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportMobilePanHistories(
        this.mobilePanHistoriesCurrentPage,
        this.mobilePanHistoriesPerPage,
        this.mobilePanHistoryFilterKey,
        this.mobilePanHistoryFilterValue,
        this.mobilePanHistoryStartDateFilter,
        this.mobilePanHistoryEndDateFilter,
        this.mobilePanHistoriesShouldExport,
        this.mobilePanHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.mobilePanHistoriesShouldExport = false;
          this.mobilePanHistoriesExportFormat = '';
          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportMobilePanHistoriesInExcel() {
    this.mobilePanHistoriesShouldExport = true;
    this.mobilePanHistoriesExportFormat = 'xlsx';
    this.exportMobilePanHistories();
  }

  exportMobilePanHistoriesInCsv() {
    this.mobilePanHistoriesShouldExport = true;
    this.mobilePanHistoriesExportFormat = 'csv';
    this.exportMobilePanHistories();
  }

  exportMobilePanHistoriesInPdf() {
    this.mobilePanHistoriesShouldExport = true;
    this.mobilePanHistoriesExportFormat = 'pdf';
    this.exportMobilePanHistories();
  }

  onMobilePanHistoriesPageChange(page: any) {
    this.mobilePanHistoriesSelectAll.isSelected = false;
    this.mobilePanHistoriesCurrentPage = page;
    this.getMobilePanHistories();
  }

  onMobilePanHistoryPagedClick(event: Event, mobilePanHistoryPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    this.updateMobilePanHistoryPaged(isChecked, mobilePanHistoryPaged.id);
  }

  updateMobilePanHistoryPaged(isChecked: any, searchId: any) {
    const mobilePanHistoryPagedToUpdate = this.mobilePanHistoriesPaged.find(
      (mobilePanHistoryPaged: any) => mobilePanHistoryPaged.id === searchId,
    );
    if (mobilePanHistoryPagedToUpdate) {
      mobilePanHistoryPagedToUpdate.isSelected = isChecked;
    }
  }

  deleteMobilePanHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deleteMobilePanHistories(this.selectedMobilePanHistories)
      .subscribe({
        next: () => {
          this.mobilePanHistoriesSelectAll.isSelected = false;
          this.updateMobilePanHistoriesPaged();
          this.updateMobilePanHistoriesCurrentPage();
          this.getMobilePanHistories();
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  updateMobilePanHistoriesPaged() {
    this.mobilePanHistoriesPaged = this.mobilePanHistoriesPaged.filter(
      (mobilePanHistoryPaged: any) =>
        !this.selectedMobilePanHistories.includes(
          mobilePanHistoryPaged.mobile_pan_search_id,
        ),
    );
  }

  updateMobilePanHistoriesCurrentPage() {
    if (this.mobilePanHistoriesPaged.length === 0) {
      if (this.mobilePanHistoriesHasNextPage) {
      } else if (this.mobilePanHistoriesHasPreviousPage) {
        this.mobilePanHistoriesCurrentPage--;
      }
    }
  }

  onMobilePanHistoriesSelectAllClick() {
    this.mobilePanHistoriesSelectAll.isSelected =
      !this.mobilePanHistoriesSelectAll.isSelected;
    this.mobilePanHistoriesSelectAll.isSelected
      ? this.mobilePanHistoriesPagedSelectAll()
      : this.mobilePanHistoriesPagedDeselectAll();
  }

  mobilePanHistoriesPagedSelectAll() {
    this.mobilePanHistoriesPaged = this.mobilePanHistoriesPaged.map(
      (mobilePanHistoryPaged: any) => ({
        ...mobilePanHistoryPaged,
        isSelected: true,
      }),
    );
  }

  mobilePanHistoriesPagedDeselectAll() {
    this.mobilePanHistoriesPaged = this.mobilePanHistoriesPaged.map(
      (mobilePanHistoryPaged: any) => ({
        ...mobilePanHistoryPaged,
        isSelected: false,
      }),
    );
  }

  goToMobilePanHistoryResult(mobilePanHistoryPaged: any) {
    const jobId = mobilePanHistoryPaged?.job_id;
    if (!jobId) {
      return;
    }
    this.router.navigate(['/mobile-pan-search-result/' + jobId], {
      state: { searchResult: mobilePanHistoryPaged },
    });
  }

  onMobilePanHistoryIdFilterChange(id: any) {
    const trimmedId = String(id || '').trim();
    if (trimmedId === '') {
      this.mobilePanHistoryFilterKey = '';
      this.mobilePanHistoryFilterValue = '';
      this.getMobilePanHistories();
    } else if (trimmedId.length === 1) {
      return;
    } else {
      this.mobilePanHistoryFilterKey = 'mobile_pan_search_id';
      this.mobilePanHistoryFilterValue = trimmedId;
      this.getMobilePanHistories();
    }
  }

  onMobilePanHistoryPhoneFilterChange(phone: any) {
    const trimmedPhone = String(phone || '').trim();
    if (trimmedPhone === '') {
      this.mobilePanHistoryFilterKey = '';
      this.mobilePanHistoryFilterValue = '';
      this.getMobilePanHistories();
    } else if (trimmedPhone.length === 1) {
      return;
    } else {
      this.mobilePanHistoryFilterKey = 'searched_phone';
      this.mobilePanHistoryFilterValue = trimmedPhone;
      this.getMobilePanHistories();
    }
  }

  onMobilePanHistoryStartDateFilterChange(startDate: any) {
    this.mobilePanHistoryStartDateFilter = startDate;
    if (
      this.mobilePanHistoryStartDateFilter === '' &&
      this.mobilePanHistoryEndDateFilter === ''
    ) {
      this.mobilePanHistoryFilterKey = '';
      this.mobilePanHistoryFilterValue = '';
      this.getMobilePanHistories();
    }
    if (this.mobilePanHistoryStartDateFilter && this.mobilePanHistoryEndDateFilter) {
      this.mobilePanHistoryFilterKey = 'created_on';
      this.getMobilePanHistories();
    }
  }

  onMobilePanHistoryEndDateFilterChange(endDate: any) {
    this.mobilePanHistoryEndDateFilter = endDate;
    if (
      this.mobilePanHistoryStartDateFilter === '' &&
      this.mobilePanHistoryEndDateFilter === ''
    ) {
      this.mobilePanHistoryFilterKey = '';
      this.mobilePanHistoryFilterValue = '';
      this.getMobilePanHistories();
    }
    if (this.mobilePanHistoryStartDateFilter && this.mobilePanHistoryEndDateFilter) {
      this.mobilePanHistoryFilterKey = 'created_on';
      this.getMobilePanHistories();
    }
  }

  resetMobilePanHistories() {
    this.mobilePanHistoriesCurrentPage = 1;
    this.mobilePanHistoryFilterKey = '';
    this.mobilePanHistoryFilterValue = '';
    this.mobilePanHistoryIdFilter = '';
    this.mobilePanHistoryPhoneFilter = '';
    this.mobilePanHistoryStartDateFilter = '';
    this.mobilePanHistoryEndDateFilter = '';
  }

  // PHONEPE FASTAG HISTORY

  phonepeFastagHistories: any = [];
  phonepeFastagHistoriesPaged: any = [];

  phonepeFastagHistoriesCount: any;
  phonepeFastagHistoriesPerPage = 10;
  phonepeFastagHistoriesCurrentPage = 1;
  phonepeFastagHistoriesHasPreviousPage: any;
  phonepeFastagHistoriesHasNextPage: any;

  phonepeFastagHistoriesSelectAll = { isSelected: false };

  phonepeFastagHistoryFilters: any = [
    { label: 'Id', value: 'id' },
    { label: 'Vehicle', value: 'vehicle' },
    { label: 'Date Added', value: 'date-added' },
  ];

  phonepeFastagHistoryFilterKey: any = '';
  phonepeFastagHistoryFilterValue: any = '';

  phonepeFastagHistoryIdFilter: any = '';
  phonepeFastagHistoryVehicleFilter: any = '';
  phonepeFastagHistoryStartDateFilter: any = '';
  phonepeFastagHistoryEndDateFilter: any = '';

  phonepeFastagHistoriesShouldExport: any;
  phonepeFastagHistoriesExportFormat: any;

  phonepeFastagHistoryTableThs: any = ['Id', 'Vehicle', 'Date Added', 'Time'];

  phonepeFastagHistoryTableTds: any = [
    { field: 'id' },
    { field: 'searched_vehicle' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get phonepeFastagHistoriesPagedStartAndEnd() {
    if (!this.phonepeFastagHistories?.length || !this.phonepeFastagHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.phonepeFastagHistoriesCurrentPage - 1) *
        this.phonepeFastagHistoriesPerPage +
      1;
    const end = Math.min(
      start + this.phonepeFastagHistories.length - 1,
      this.phonepeFastagHistoriesCount,
    );

    return { start, end };
  }

  get selectedPhonepeFastagHistories() {
    return this.phonepeFastagHistoriesPaged
      .filter(
        (phonepeFastagHistoryPaged: any) =>
          phonepeFastagHistoryPaged.isSelected,
      )
      .map(
        (phonepeFastagHistoryPaged: any) =>
          phonepeFastagHistoryPaged.phonepe_fastag_search_id,
      );
  }

  getPhonepeFastagHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getPhonepeFastagHistories(
        this.phonepeFastagHistoriesCurrentPage,
        this.phonepeFastagHistoriesPerPage,
        this.phonepeFastagHistoryFilterKey,
        this.phonepeFastagHistoryFilterValue,
        this.phonepeFastagHistoryStartDateFilter,
        this.phonepeFastagHistoryEndDateFilter,
        this.phonepeFastagHistoriesShouldExport,
        this.phonepeFastagHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.phonepeFastagHistories = response?.data?.data_list;
          this.phonepeFastagHistoriesPaged = this.addAdditionalKeys([
            ...this.phonepeFastagHistories,
          ]);
          this.phonepeFastagHistoriesCount = response?.data?.total_items;
          this.phonepeFastagHistoriesHasPreviousPage =
            response?.data?.has_previous;
          this.phonepeFastagHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportPhonepeFastagHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportPhonepeFastagHistories(
        this.phonepeFastagHistoriesCurrentPage,
        this.phonepeFastagHistoriesPerPage,
        this.phonepeFastagHistoryFilterKey,
        this.phonepeFastagHistoryFilterValue,
        this.phonepeFastagHistoryStartDateFilter,
        this.phonepeFastagHistoryEndDateFilter,
        this.phonepeFastagHistoriesShouldExport,
        this.phonepeFastagHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.phonepeFastagHistoriesShouldExport = false;
          this.phonepeFastagHistoriesExportFormat = '';
          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportPhonepeFastagHistoriesInExcel() {
    this.phonepeFastagHistoriesShouldExport = true;
    this.phonepeFastagHistoriesExportFormat = 'xlsx';
    this.exportPhonepeFastagHistories();
  }

  exportPhonepeFastagHistoriesInCsv() {
    this.phonepeFastagHistoriesShouldExport = true;
    this.phonepeFastagHistoriesExportFormat = 'csv';
    this.exportPhonepeFastagHistories();
  }

  exportPhonepeFastagHistoriesInPdf() {
    this.phonepeFastagHistoriesShouldExport = true;
    this.phonepeFastagHistoriesExportFormat = 'pdf';
    this.exportPhonepeFastagHistories();
  }

  onPhonepeFastagHistoriesPageChange(page: any) {
    this.phonepeFastagHistoriesSelectAll.isSelected = false;
    this.phonepeFastagHistoriesCurrentPage = page;
    this.getPhonepeFastagHistories();
  }

  onPhonepeFastagHistoryPagedClick(
    event: Event,
    phonepeFastagHistoryPaged: any,
  ) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    this.updatePhonepeFastagHistoryPaged(
      isChecked,
      phonepeFastagHistoryPaged.id,
    );
  }

  updatePhonepeFastagHistoryPaged(isChecked: any, searchId: any) {
    const phonepeFastagHistoryPagedToUpdate =
      this.phonepeFastagHistoriesPaged.find(
        (phonepeFastagHistoryPaged: any) =>
          phonepeFastagHistoryPaged.id === searchId,
      );
    if (phonepeFastagHistoryPagedToUpdate) {
      phonepeFastagHistoryPagedToUpdate.isSelected = isChecked;
    }
  }

  deletePhonepeFastagHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deletePhonepeFastagHistories(this.selectedPhonepeFastagHistories)
      .subscribe({
        next: () => {
          this.phonepeFastagHistoriesSelectAll.isSelected = false;
          this.updatePhonepeFastagHistoriesPaged();
          this.updatePhonepeFastagHistoriesCurrentPage();
          this.getPhonepeFastagHistories();
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  updatePhonepeFastagHistoriesPaged() {
    this.phonepeFastagHistoriesPaged = this.phonepeFastagHistoriesPaged.filter(
      (phonepeFastagHistoryPaged: any) =>
        !this.selectedPhonepeFastagHistories.includes(
          phonepeFastagHistoryPaged.phonepe_fastag_search_id,
        ),
    );
  }

  updatePhonepeFastagHistoriesCurrentPage() {
    if (this.phonepeFastagHistoriesPaged.length === 0) {
      if (this.phonepeFastagHistoriesHasNextPage) {
      } else if (this.phonepeFastagHistoriesHasPreviousPage) {
        this.phonepeFastagHistoriesCurrentPage--;
      }
    }
  }

  onPhonepeFastagHistoriesSelectAllClick() {
    this.phonepeFastagHistoriesSelectAll.isSelected =
      !this.phonepeFastagHistoriesSelectAll.isSelected;
    this.phonepeFastagHistoriesSelectAll.isSelected
      ? this.phonepeFastagHistoriesPagedSelectAll()
      : this.phonepeFastagHistoriesPagedDeselectAll();
  }

  phonepeFastagHistoriesPagedSelectAll() {
    this.phonepeFastagHistoriesPaged = this.phonepeFastagHistoriesPaged.map(
      (phonepeFastagHistoryPaged: any) => ({
        ...phonepeFastagHistoryPaged,
        isSelected: true,
      }),
    );
  }

  phonepeFastagHistoriesPagedDeselectAll() {
    this.phonepeFastagHistoriesPaged = this.phonepeFastagHistoriesPaged.map(
      (phonepeFastagHistoryPaged: any) => ({
        ...phonepeFastagHistoryPaged,
        isSelected: false,
      }),
    );
  }

  goToPhonepeFastagHistoryResult(phonepeFastagHistoryPaged: any) {
    const jobId = phonepeFastagHistoryPaged?.job_id;
    if (!jobId) {
      return;
    }
    this.router.navigate(['/phonepe-fastag-search-result/' + jobId], {
      state: { searchResult: phonepeFastagHistoryPaged },
    });
  }

  onPhonepeFastagHistoryIdFilterChange(id: string) {
    const trimmedId = (id || '').trim();
    if (!trimmedId) {
      this.phonepeFastagHistoryFilterKey = '';
      this.phonepeFastagHistoryFilterValue = '';
      this.getPhonepeFastagHistories();
      return;
    }
    this.phonepeFastagHistoriesCurrentPage = 1;
    this.phonepeFastagHistoryFilterKey = 'phonepe_fastag_search_id';
    this.phonepeFastagHistoryFilterValue = trimmedId;
    this.getPhonepeFastagHistories();
  }

  onPhonepeFastagHistoryVehicleFilterChange(vehicle: string) {
    const trimmedVehicle = (vehicle || '').trim();
    if (!trimmedVehicle) {
      this.phonepeFastagHistoryFilterKey = '';
      this.phonepeFastagHistoryFilterValue = '';
      this.getPhonepeFastagHistories();
      return;
    }
    this.phonepeFastagHistoriesCurrentPage = 1;
    this.phonepeFastagHistoryFilterKey = 'searched_vehicle';
    this.phonepeFastagHistoryFilterValue = trimmedVehicle;
    this.getPhonepeFastagHistories();
  }

  onPhonepeFastagHistoryStartDateFilterChange(startDate: string) {
    this.phonepeFastagHistoryStartDateFilter = startDate;
    if (
      this.phonepeFastagHistoryStartDateFilter === '' &&
      this.phonepeFastagHistoryEndDateFilter === ''
    ) {
      this.phonepeFastagHistoryFilterKey = '';
      this.phonepeFastagHistoryFilterValue = '';
    }
    if (
      this.phonepeFastagHistoryStartDateFilter &&
      this.phonepeFastagHistoryEndDateFilter
    ) {
      this.phonepeFastagHistoryFilterKey = 'created_on';
      this.getPhonepeFastagHistories();
    }
  }

  onPhonepeFastagHistoryEndDateFilterChange(endDate: string) {
    this.phonepeFastagHistoryEndDateFilter = endDate;
    if (
      this.phonepeFastagHistoryStartDateFilter === '' &&
      this.phonepeFastagHistoryEndDateFilter === ''
    ) {
      this.phonepeFastagHistoryFilterKey = '';
      this.phonepeFastagHistoryFilterValue = '';
    }
    if (
      this.phonepeFastagHistoryStartDateFilter &&
      this.phonepeFastagHistoryEndDateFilter
    ) {
      this.phonepeFastagHistoryFilterKey = 'created_on';
      this.getPhonepeFastagHistories();
    }
  }

  resetPhonepeFastagHistories() {
    this.phonepeFastagHistoriesCurrentPage = 1;
    this.phonepeFastagHistoryFilterKey = '';
    this.phonepeFastagHistoryFilterValue = '';
    this.phonepeFastagHistoryIdFilter = '';
    this.phonepeFastagHistoryVehicleFilter = '';
    this.phonepeFastagHistoryStartDateFilter = '';
    this.phonepeFastagHistoryEndDateFilter = '';
  }

  // PHONEPE UPI HISTORY

  phonepeUpiHistories: any = [];
  phonepeUpiHistoriesPaged: any = [];

  phonepeUpiHistoriesCount: any;
  phonepeUpiHistoriesPerPage = 10;
  phonepeUpiHistoriesCurrentPage = 1;
  phonepeUpiHistoriesHasPreviousPage: any;
  phonepeUpiHistoriesHasNextPage: any;

  phonepeUpiHistoriesSelectAll = { isSelected: false };

  phonepeUpiHistoryFilters: any = [
    { label: 'Id', value: 'id' },
    { label: 'VPA', value: 'vpa' },
    { label: 'Date Added', value: 'date-added' },
  ];

  phonepeUpiHistoryFilterKey: any = '';
  phonepeUpiHistoryFilterValue: any = '';

  phonepeUpiHistoryIdFilter: any = '';
  phonepeUpiHistoryVpaFilter: any = '';
  phonepeUpiHistoryStartDateFilter: any = '';
  phonepeUpiHistoryEndDateFilter: any = '';

  phonepeUpiHistoriesShouldExport: any;
  phonepeUpiHistoriesExportFormat: any;

  phonepeUpiHistoryTableThs: any = ['Id', 'VPA', 'Date Added', 'Time'];

  phonepeUpiHistoryTableTds: any = [
    { field: 'id' },
    { field: 'searched_upi' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get phonepeUpiHistoriesPagedStartAndEnd() {
    if (!this.phonepeUpiHistories?.length || !this.phonepeUpiHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.phonepeUpiHistoriesCurrentPage - 1) *
        this.phonepeUpiHistoriesPerPage +
      1;
    const end = Math.min(
      start + this.phonepeUpiHistories.length - 1,
      this.phonepeUpiHistoriesCount,
    );

    return { start, end };
  }

  get selectedPhonepeUpiHistories() {
    return this.phonepeUpiHistoriesPaged
      .filter(
        (phonepeUpiHistoryPaged: any) => phonepeUpiHistoryPaged.isSelected,
      )
      .map(
        (phonepeUpiHistoryPaged: any) =>
          phonepeUpiHistoryPaged.phonepe_upi_search_id,
      );
  }

  getPhonepeUpiHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getPhonepeUpiHistories(
        this.phonepeUpiHistoriesCurrentPage,
        this.phonepeUpiHistoriesPerPage,
        this.phonepeUpiHistoryFilterKey,
        this.phonepeUpiHistoryFilterValue,
        this.phonepeUpiHistoryStartDateFilter,
        this.phonepeUpiHistoryEndDateFilter,
        this.phonepeUpiHistoriesShouldExport,
        this.phonepeUpiHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.phonepeUpiHistories = response?.data?.data_list;
          this.phonepeUpiHistoriesPaged = this.addAdditionalKeys([
            ...this.phonepeUpiHistories,
          ]);
          this.phonepeUpiHistoriesCount = response?.data?.total_items;
          this.phonepeUpiHistoriesHasPreviousPage =
            response?.data?.has_previous;
          this.phonepeUpiHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportPhonepeUpiHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportPhonepeUpiHistories(
        this.phonepeUpiHistoriesCurrentPage,
        this.phonepeUpiHistoriesPerPage,
        this.phonepeUpiHistoryFilterKey,
        this.phonepeUpiHistoryFilterValue,
        this.phonepeUpiHistoryStartDateFilter,
        this.phonepeUpiHistoryEndDateFilter,
        this.phonepeUpiHistoriesShouldExport,
        this.phonepeUpiHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.phonepeUpiHistoriesShouldExport = false;
          this.phonepeUpiHistoriesExportFormat = '';
          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportPhonepeUpiHistoriesInExcel() {
    this.phonepeUpiHistoriesShouldExport = true;
    this.phonepeUpiHistoriesExportFormat = 'xlsx';
    this.exportPhonepeUpiHistories();
  }

  exportPhonepeUpiHistoriesInCsv() {
    this.phonepeUpiHistoriesShouldExport = true;
    this.phonepeUpiHistoriesExportFormat = 'csv';
    this.exportPhonepeUpiHistories();
  }

  exportPhonepeUpiHistoriesInPdf() {
    this.phonepeUpiHistoriesShouldExport = true;
    this.phonepeUpiHistoriesExportFormat = 'pdf';
    this.exportPhonepeUpiHistories();
  }

  onPhonepeUpiHistoriesPageChange(page: any) {
    this.phonepeUpiHistoriesSelectAll.isSelected = false;
    this.phonepeUpiHistoriesCurrentPage = page;
    this.getPhonepeUpiHistories();
  }

  onPhonepeUpiHistoryPagedClick(event: Event, phonepeUpiHistoryPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    this.updatePhonepeUpiHistoryPaged(isChecked, phonepeUpiHistoryPaged.id);
  }

  updatePhonepeUpiHistoryPaged(isChecked: any, searchId: any) {
    const phonepeUpiHistoryPagedToUpdate = this.phonepeUpiHistoriesPaged.find(
      (phonepeUpiHistoryPaged: any) => phonepeUpiHistoryPaged.id === searchId,
    );
    if (phonepeUpiHistoryPagedToUpdate) {
      phonepeUpiHistoryPagedToUpdate.isSelected = isChecked;
    }
  }

  deletePhonepeUpiHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deletePhonepeUpiHistories(this.selectedPhonepeUpiHistories)
      .subscribe({
        next: () => {
          this.phonepeUpiHistoriesSelectAll.isSelected = false;
          this.updatePhonepeUpiHistoriesPaged();
          this.updatePhonepeUpiHistoriesCurrentPage();
          this.getPhonepeUpiHistories();
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  updatePhonepeUpiHistoriesPaged() {
    this.phonepeUpiHistoriesPaged = this.phonepeUpiHistoriesPaged.filter(
      (phonepeUpiHistoryPaged: any) =>
        !this.selectedPhonepeUpiHistories.includes(
          phonepeUpiHistoryPaged.phonepe_upi_search_id,
        ),
    );
  }

  updatePhonepeUpiHistoriesCurrentPage() {
    if (this.phonepeUpiHistoriesPaged.length === 0) {
      if (this.phonepeUpiHistoriesHasNextPage) {
      } else if (this.phonepeUpiHistoriesHasPreviousPage) {
        this.phonepeUpiHistoriesCurrentPage--;
      }
    }
  }

  onPhonepeUpiHistoriesSelectAllClick() {
    this.phonepeUpiHistoriesSelectAll.isSelected =
      !this.phonepeUpiHistoriesSelectAll.isSelected;
    this.phonepeUpiHistoriesSelectAll.isSelected
      ? this.phonepeUpiHistoriesPagedSelectAll()
      : this.phonepeUpiHistoriesPagedDeselectAll();
  }

  phonepeUpiHistoriesPagedSelectAll() {
    this.phonepeUpiHistoriesPaged = this.phonepeUpiHistoriesPaged.map(
      (phonepeUpiHistoryPaged: any) => ({
        ...phonepeUpiHistoryPaged,
        isSelected: true,
      }),
    );
  }

  phonepeUpiHistoriesPagedDeselectAll() {
    this.phonepeUpiHistoriesPaged = this.phonepeUpiHistoriesPaged.map(
      (phonepeUpiHistoryPaged: any) => ({
        ...phonepeUpiHistoryPaged,
        isSelected: false,
      }),
    );
  }

  goToPhonepeUpiHistoryResult(phonepeUpiHistoryPaged: any) {
    const jobId = phonepeUpiHistoryPaged?.job_id;
    if (!jobId) {
      return;
    }
    this.router.navigate(['/phonepe-upi-search-result/' + jobId], {
      state: { searchResult: phonepeUpiHistoryPaged },
    });
  }

  onPhonepeUpiHistoryIdFilterChange(id: string) {
    const trimmedId = (id || '').trim();
    if (!trimmedId) {
      this.phonepeUpiHistoryFilterKey = '';
      this.phonepeUpiHistoryFilterValue = '';
      this.getPhonepeUpiHistories();
      return;
    }
    this.phonepeUpiHistoriesCurrentPage = 1;
    this.phonepeUpiHistoryFilterKey = 'phonepe_upi_search_id';
    this.phonepeUpiHistoryFilterValue = trimmedId;
    this.getPhonepeUpiHistories();
  }

  onPhonepeUpiHistoryVpaFilterChange(vpa: string) {
    const trimmedVpa = (vpa || '').trim();
    if (!trimmedVpa) {
      this.phonepeUpiHistoryFilterKey = '';
      this.phonepeUpiHistoryFilterValue = '';
      this.getPhonepeUpiHistories();
      return;
    }
    this.phonepeUpiHistoriesCurrentPage = 1;
    this.phonepeUpiHistoryFilterKey = 'searched_upi';
    this.phonepeUpiHistoryFilterValue = trimmedVpa;
    this.getPhonepeUpiHistories();
  }

  onPhonepeUpiHistoryStartDateFilterChange(startDate: string) {
    this.phonepeUpiHistoryStartDateFilter = startDate;
    if (
      this.phonepeUpiHistoryStartDateFilter === '' &&
      this.phonepeUpiHistoryEndDateFilter === ''
    ) {
      this.phonepeUpiHistoryFilterKey = '';
      this.phonepeUpiHistoryFilterValue = '';
    }
    if (
      this.phonepeUpiHistoryStartDateFilter &&
      this.phonepeUpiHistoryEndDateFilter
    ) {
      this.phonepeUpiHistoryFilterKey = 'created_on';
      this.getPhonepeUpiHistories();
    }
  }

  onPhonepeUpiHistoryEndDateFilterChange(endDate: string) {
    this.phonepeUpiHistoryEndDateFilter = endDate;
    if (
      this.phonepeUpiHistoryStartDateFilter === '' &&
      this.phonepeUpiHistoryEndDateFilter === ''
    ) {
      this.phonepeUpiHistoryFilterKey = '';
      this.phonepeUpiHistoryFilterValue = '';
    }
    if (
      this.phonepeUpiHistoryStartDateFilter &&
      this.phonepeUpiHistoryEndDateFilter
    ) {
      this.phonepeUpiHistoryFilterKey = 'created_on';
      this.getPhonepeUpiHistories();
    }
  }

  resetPhonepeUpiHistories() {
    this.phonepeUpiHistoriesCurrentPage = 1;
    this.phonepeUpiHistoryFilterKey = '';
    this.phonepeUpiHistoryFilterValue = '';
    this.phonepeUpiHistoryIdFilter = '';
    this.phonepeUpiHistoryVpaFilter = '';
    this.phonepeUpiHistoryStartDateFilter = '';
    this.phonepeUpiHistoryEndDateFilter = '';
  }


  // VIRTUAL NUMBER HISTORIES

  virtualNumberHistories: any = [];
  virtualNumberHistoriesPaged: any = [];

  virtualNumberHistoriesCount: any;
  virtualNumberHistoriesPerPage = 10;
  virtualNumberHistoriesCurrentPage = 1;
  virtualNumberHistoriesHasPreviousPage: any;
  virtualNumberHistoriesHasNextPage: any;

  virtualNumberHistoriesSelectAll = { isSelected: false };

  virtualNumberHistoryFilters: any = [
    { label: 'Id', value: 'id' },
    { label: 'Virtual Number', value: 'virtual-number' },
    { label: 'Date Added', value: 'date-added' },
  ];

  virtualNumberHistoryFilterKey: any = '';
  virtualNumberHistoryFilterValue: any = '';

  virtualNumberHistoryIdFilter: any = '';
  virtualNumberHistoryVirtualNumberFilter: any = '';
  virtualNumberHistoryStartDateFilter: any = '';
  virtualNumberHistoryEndDateFilter: any = '';

  virtualNumberHistoriesShouldExport: any;
  virtualNumberHistoriesExportFormat: any;

  virtualNumberHistoryTableThs: any = [
    'Id',
    'Virtual Number',
    'Date Added',
    'Time',
  ];

  virtualNumberHistoryTableTds: any = [
    { field: 'id' },
    { field: 'virtual_number' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get virtualNumberHistoriesPagedStartAndEnd() {
    if (
      !this.virtualNumberHistories?.length ||
      !this.virtualNumberHistoriesCount
    ) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.virtualNumberHistoriesCurrentPage - 1) *
      this.virtualNumberHistoriesPerPage +
      1;
    const end = Math.min(
      start + this.virtualNumberHistories.length - 1,
      this.virtualNumberHistoriesCount,
    );

    return { start, end };
  }

  get selectedVirtualNumberHistories() {
    return this.virtualNumberHistoriesPaged
      .filter(
        (virtualNumberHistoryPaged: any) =>
          virtualNumberHistoryPaged.isSelected,
      )
      .map(
        (virtualNumberHistoryPaged: any) =>
          virtualNumberHistoryPaged.virtual_number_search_id,
      );
  }

  getVirtualNumberHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getVirtualNumberHistories(
        this.virtualNumberHistoriesCurrentPage,
        this.virtualNumberHistoriesPerPage,
        this.virtualNumberHistoryFilterKey,
        this.virtualNumberHistoryFilterValue,
        this.virtualNumberHistoryStartDateFilter,
        this.virtualNumberHistoryEndDateFilter,
        this.virtualNumberHistoriesShouldExport,
        this.virtualNumberHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('GET VIRTUAL NUMBER HISTORIES API RESPONSE: ', response);

          this.virtualNumberHistories = response?.data?.data_list;
          this.virtualNumberHistoriesPaged = this.addAdditionalKeys([
            ...this.virtualNumberHistories,
          ]);
          this.virtualNumberHistoriesCount = response?.data?.total_items;
          this.virtualNumberHistoriesHasPreviousPage =
            response?.data?.has_previous;
          this.virtualNumberHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportVirtualNumberHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportVirtualNumberHistories(
        this.virtualNumberHistoriesCurrentPage,
        this.virtualNumberHistoriesPerPage,
        this.virtualNumberHistoryFilterKey,
        this.virtualNumberHistoryFilterValue,
        this.virtualNumberHistoryStartDateFilter,
        this.virtualNumberHistoryEndDateFilter,
        this.virtualNumberHistoriesShouldExport,
        this.virtualNumberHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('EXPORT VIRTUAL NUMBER HISTORIES API RESPONSE: ', response);

          this.virtualNumberHistoriesShouldExport = false;
          this.virtualNumberHistoriesExportFormat = '';

          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportVirtualNumberHistoriesInExcel() {
    this.virtualNumberHistoriesShouldExport = true;
    this.virtualNumberHistoriesExportFormat = 'xlsx';

    this.exportVirtualNumberHistories();
  }

  exportVirtualNumberHistoriesInCsv() {
    this.virtualNumberHistoriesShouldExport = true;
    this.virtualNumberHistoriesExportFormat = 'csv';

    this.exportVirtualNumberHistories();
  }

  exportVirtualNumberHistoriesInPdf() {
    this.virtualNumberHistoriesShouldExport = true;
    this.virtualNumberHistoriesExportFormat = 'pdf';

    this.exportVirtualNumberHistories();
  }

  onVirtualNumberHistoriesPageChange(page: any) {
    this.virtualNumberHistoriesSelectAll.isSelected = false;
    this.virtualNumberHistoriesCurrentPage = page;

    this.getVirtualNumberHistories();
  }

  onVirtualNumberHistoryPagedClick(
    event: Event,
    virtualNumberHistoryPaged: any,
  ) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateVirtualNumberHistoryPaged(
      isChecked,
      virtualNumberHistoryPaged.id,
    );
  }

  updateVirtualNumberHistoryPaged(isChecked: any, searchId: any) {
    const virtualNumberHistoryPagedToUpdate =
      this.virtualNumberHistoriesPaged.find(
        (virtualNumberHistoryPaged: any) =>
          virtualNumberHistoryPaged.id === searchId,
      );

    if (virtualNumberHistoryPagedToUpdate) {
      virtualNumberHistoryPagedToUpdate.isSelected = isChecked;
    }
  }

  deleteVirtualNumberHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deleteVirtualNumberHistories(this.selectedVirtualNumberHistories)
      .subscribe({
        next: (response) => {
          // console.log('DELETE VIRTUAL NUMBER HISTORIES API RESPONSE: ', response);

          this.virtualNumberHistoriesSelectAll.isSelected = false;

          this.updateVirtualNumberHistoriesPaged();
          this.updateVirtualNumberHistoriesCurrentPage();

          this.getVirtualNumberHistories();
        },
        error: (error) => {
          console.error('DELETE VIRTUAL NUMBER HISTORIES API ERROR: ', error);
          this.isLoading = false;
        },
      });
  }

  updateVirtualNumberHistoriesPaged() {
    this.virtualNumberHistoriesPaged = this.virtualNumberHistoriesPaged.filter(
      (virtualNumberHistoryPaged: any) =>
        !this.selectedVirtualNumberHistories.includes(
          virtualNumberHistoryPaged.virtual_number_search_id,
        ),
    );
  }

  updateVirtualNumberHistoriesCurrentPage() {
    if (this.virtualNumberHistoriesPaged.length === 0) {
      if (this.virtualNumberHistoriesHasNextPage) {
      } else {
        if (this.virtualNumberHistoriesHasPreviousPage) {
          this.virtualNumberHistoriesCurrentPage--;
        }
      }
    }
  }

  onVirtualNumberHistoriesSelectAllClick() {
    this.virtualNumberHistoriesSelectAll.isSelected =
      !this.virtualNumberHistoriesSelectAll.isSelected;

    this.virtualNumberHistoriesSelectAll.isSelected
      ? this.selectAllVirtualNumberHistoriesPaged()
      : this.deselectAllVirtualNumberHistoriesPaged();
  }

  selectAllVirtualNumberHistoriesPaged() {
    this.virtualNumberHistoriesPaged = this.virtualNumberHistoriesPaged.map(
      (virtualNumberHistoryPaged: any) => ({
        ...virtualNumberHistoryPaged,
        isSelected: true,
      }),
    );
  }

  deselectAllVirtualNumberHistoriesPaged() {
    this.virtualNumberHistoriesPaged = this.virtualNumberHistoriesPaged.map(
      (virtualNumberHistoryPaged: any) => ({
        ...virtualNumberHistoryPaged,
        isSelected: false,
      }),
    );
  }

  onVirtualNumberHistoryIdFilterChange(id: any) {
    this.virtualNumberHistoryIdSubject.next(id);
  }

  private onVirtualNumberHistoryIdFilterChangeDebounced(id: any) {
    const trimmedId = id.trim();

    if (trimmedId === '') {
      this.virtualNumberHistoryFilterKey = '';
      this.virtualNumberHistoryFilterValue = '';

      this.getVirtualNumberHistories();
    } else if (trimmedId.length === 1) {
      return;
    } else {
      this.virtualNumberHistoryFilterKey = 'id';
      this.virtualNumberHistoryFilterValue = trimmedId;

      this.getVirtualNumberHistories();
    }
  }

  onVirtualNumberHistoryVirtualNumberFilterChange(virtualNumber: any) {
    this.virtualNumberHistoryVirtualNumberSubject.next(virtualNumber);
  }

  private onVirtualNumberHistoryVirtualNumberFilterChangeDebounced(virtualNumber: any) {
    const trimmedVirtualNumber = virtualNumber.trim();

    if (trimmedVirtualNumber === '') {
      this.virtualNumberHistoryFilterKey = '';
      this.virtualNumberHistoryFilterValue = '';

      this.getVirtualNumberHistories();
    } else if (trimmedVirtualNumber.length === 1) {
      return;
    } else {
      this.virtualNumberHistoryFilterKey = 'virtual_number';
      this.virtualNumberHistoryFilterValue = trimmedVirtualNumber;

      this.getVirtualNumberHistories();
    }
  }

  onVirtualNumberHistoryStartDateFilterChange(startDate: any) {
    this.virtualNumberHistoryStartDateFilter = startDate;

    if (
      this.virtualNumberHistoryStartDateFilter === '' &&
      this.virtualNumberHistoryEndDateFilter === ''
    ) {
      this.virtualNumberHistoryFilterKey = '';
      this.virtualNumberHistoryFilterValue = '';

      this.getVirtualNumberHistories();
    }

    if (
      this.virtualNumberHistoryStartDateFilter &&
      this.virtualNumberHistoryEndDateFilter
    ) {
      this.virtualNumberHistoryFilterKey = 'created_on';
      this.getVirtualNumberHistories();
    }
  }

  onVirtualNumberHistoryEndDateFilterChange(endDate: any) {
    this.virtualNumberHistoryEndDateFilter = endDate;

    if (
      this.virtualNumberHistoryStartDateFilter === '' &&
      this.virtualNumberHistoryEndDateFilter === ''
    ) {
      this.virtualNumberHistoryFilterKey = '';
      this.virtualNumberHistoryFilterValue = '';

      this.getVirtualNumberHistories();
    }

    if (
      this.virtualNumberHistoryStartDateFilter &&
      this.virtualNumberHistoryEndDateFilter
    ) {
      this.virtualNumberHistoryFilterKey = 'created_on';
      this.getVirtualNumberHistories();
    }
  }

  resetVirtualNumberHistories() {
    this.virtualNumberHistoriesCurrentPage = 1;

    this.virtualNumberHistoryFilterKey = '';
    this.virtualNumberHistoryFilterValue = '';

    this.virtualNumberHistoryIdFilter = '';
    this.virtualNumberHistoryVirtualNumberFilter = '';
    this.virtualNumberHistoryStartDateFilter = '';
    this.virtualNumberHistoryEndDateFilter = '';
  }

  // ADVANCED SEARCH HISTORIES

  advancedSearchHistories: any = [];
  advancedSearchHistoriesPaged: any = [];

  advancedSearchHistoriesCount: any;
  advancedSearchHistoriesPerPage = 10;
  advancedSearchHistoriesCurrentPage = 1;
  advancedSearchHistoriesHasPreviousPage: any;
  advancedSearchHistoriesHasNextPage: any;

  advancedSearchHistoriesSelectAll = { isSelected: false };

  advancedSearchHistoryFilters: any = [
    { label: 'Id', value: 'id' },
    { label: 'Search Name', value: 'search-name' },
    { label: 'Date Added', value: 'date-added' },
  ];

  advancedSearchHistoryFilterKey: any = '';
  advancedSearchHistoryFilterValue: any = '';

  advancedSearchHistoryIdFilter: any = '';
  advancedSearchHistorySearchNameFilter: any = '';
  advancedSearchHistoryStartDateFilter: any = '';
  advancedSearchHistoryEndDateFilter: any = '';

  advancedSearchHistoriesShouldExport: any;
  advancedSearchHistoriesExportFormat: any;

  advancedSearchHistoryTableThs: any = [
    'Id',
    'Search Name',
    'Date Added',
    'Time',
  ];

  advancedSearchHistoryTableTds: any = [
    { field: 'id' },
    { field: 'search_name' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get advancedSearchHistoriesPagedStartAndEnd() {
    if (
      !this.advancedSearchHistories?.length ||
      !this.advancedSearchHistoriesCount
    ) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.advancedSearchHistoriesCurrentPage - 1) *
      this.advancedSearchHistoriesPerPage +
      1;
    const end = Math.min(
      start + this.advancedSearchHistories.length - 1,
      this.advancedSearchHistoriesCount,
    );

    return { start, end };
  }

  get selectedAdvancedSearchHistories() {
    return this.advancedSearchHistoriesPaged
      .filter(
        (advancedSearchHistoryPaged: any) =>
          advancedSearchHistoryPaged.isSelected,
      )
      .map(
        (advancedSearchHistoryPaged: any) =>
          advancedSearchHistoryPaged.advanced_search_id,
      );
  }

  getAdvancedSearchHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getAdvancedSearchHistories(
        this.advancedSearchHistoriesCurrentPage,
        this.advancedSearchHistoriesPerPage,
        this.advancedSearchHistoryFilterKey,
        this.advancedSearchHistoryFilterValue,
        this.advancedSearchHistoryStartDateFilter,
        this.advancedSearchHistoryEndDateFilter,
        this.advancedSearchHistoriesShouldExport,
        this.advancedSearchHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('GET ADVANCED SEARCH HISTORIES API RESPONSE: ', response);

          this.advancedSearchHistories = response?.data?.data_list;
          this.advancedSearchHistoriesPaged = this.addAdditionalKeys([
            ...this.advancedSearchHistories,
          ]);
          this.advancedSearchHistoriesCount = response?.data?.total_items;
          this.advancedSearchHistoriesHasPreviousPage =
            response?.data?.has_previous;
          this.advancedSearchHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportAdvancedSearchHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportAdvancedSearchHistories(
        this.advancedSearchHistoriesCurrentPage,
        this.advancedSearchHistoriesPerPage,
        this.advancedSearchHistoryFilterKey,
        this.advancedSearchHistoryFilterValue,
        this.advancedSearchHistoryStartDateFilter,
        this.advancedSearchHistoryEndDateFilter,
        this.advancedSearchHistoriesShouldExport,
        this.advancedSearchHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          // console.log('EXPORT ADVANCED SEARCH HISTORIES API RESPONSE: ', response);

          this.advancedSearchHistoriesShouldExport = false;
          this.advancedSearchHistoriesExportFormat = '';

          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportAdvancedSearchHistoriesInExcel() {
    this.advancedSearchHistoriesShouldExport = true;
    this.advancedSearchHistoriesExportFormat = 'xlsx';

    this.exportAdvancedSearchHistories();
  }

  exportAdvancedSearchHistoriesInCsv() {
    this.advancedSearchHistoriesShouldExport = true;
    this.advancedSearchHistoriesExportFormat = 'csv';

    this.exportAdvancedSearchHistories();
  }

  exportAdvancedSearchHistoriesInPdf() {
    this.advancedSearchHistoriesShouldExport = true;
    this.advancedSearchHistoriesExportFormat = 'pdf';

    this.exportAdvancedSearchHistories();
  }

  onAdvancedSearchHistoriesPageChange(page: any) {
    this.advancedSearchHistoriesSelectAll.isSelected = false;
    this.advancedSearchHistoriesCurrentPage = page;

    this.getAdvancedSearchHistories();
  }

  onAdvancedSearchHistoryPagedClick(
    event: Event,
    advancedSearchHistoryPaged: any,
  ) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateAdvancedSearchHistoryPaged(
      isChecked,
      advancedSearchHistoryPaged.id,
    );
  }

  updateAdvancedSearchHistoryPaged(isChecked: any, searchId: any) {
    const advancedSearchHistoryPagedToUpdate =
      this.advancedSearchHistoriesPaged.find(
        (advancedSearchHistoryPaged: any) =>
          advancedSearchHistoryPaged.id === searchId,
      );

    if (advancedSearchHistoryPagedToUpdate) {
      advancedSearchHistoryPagedToUpdate.isSelected = isChecked;
    }
  }

  deleteAdvancedSearchHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deleteAdvancedSearchHistories(this.selectedAdvancedSearchHistories)
      .subscribe({
        next: (response) => {
          // console.log('DELETE ADVANCED SEARCH HISTORIES API RESPONSE: ', response);

          this.advancedSearchHistoriesSelectAll.isSelected = false;

          this.updateAdvancedSearchHistoriesPaged();
          this.updateAdvancedSearchHistoriesCurrentPage();

          this.getAdvancedSearchHistories();
        },
        error: (error) => {
          console.error('DELETE ADVANCED SEARCH HISTORIES API ERROR: ', error);
          this.isLoading = false;
        },
      });
  }

  updateAdvancedSearchHistoriesPaged() {
    this.advancedSearchHistoriesPaged =
      this.advancedSearchHistoriesPaged.filter(
        (advancedSearchHistoryPaged: any) =>
          !this.selectedAdvancedSearchHistories.includes(
            advancedSearchHistoryPaged.advanced_search_id,
          ),
      );
  }

  updateAdvancedSearchHistoriesCurrentPage() {
    if (this.advancedSearchHistoriesPaged.length === 0) {
      if (this.advancedSearchHistoriesHasNextPage) {
      } else {
        if (this.advancedSearchHistoriesHasPreviousPage) {
          this.advancedSearchHistoriesCurrentPage--;
        }
      }
    }
  }

  onAdvancedSearchHistoriesSelectAllClick() {
    this.advancedSearchHistoriesSelectAll.isSelected =
      !this.advancedSearchHistoriesSelectAll.isSelected;

    this.advancedSearchHistoriesSelectAll.isSelected
      ? this.selectAllAdvancedSearchHistoriesPaged()
      : this.deselectAllAdvancedSearchHistoriesPaged();
  }

  selectAllAdvancedSearchHistoriesPaged() {
    this.advancedSearchHistoriesPaged = this.advancedSearchHistoriesPaged.map(
      (advancedSearchHistoryPaged: any) => ({
        ...advancedSearchHistoryPaged,
        isSelected: true,
      }),
    );
  }

  deselectAllAdvancedSearchHistoriesPaged() {
    this.advancedSearchHistoriesPaged = this.advancedSearchHistoriesPaged.map(
      (advancedSearchHistoryPaged: any) => ({
        ...advancedSearchHistoryPaged,
        isSelected: false,
      }),
    );
  }

  onAdvancedSearchHistoryIdFilterChange(id: any) {
    this.advancedSearchHistoryIdSubject.next(id);
  }

  private onAdvancedSearchHistoryIdFilterChangeDebounced(id: any) {
    const trimmedId = id.trim();

    if (trimmedId === '') {
      this.advancedSearchHistoryFilterKey = '';
      this.advancedSearchHistoryFilterValue = '';

      this.getAdvancedSearchHistories();
    } else if (trimmedId.length === 1) {
      return;
    } else {
      this.advancedSearchHistoryFilterKey = 'id';
      this.advancedSearchHistoryFilterValue = trimmedId;

      this.getAdvancedSearchHistories();
    }
  }

  onAdvancedSearchHistorySearchNameFilterChange(searchName: any) {
    this.advancedSearchHistorySearchNameSubject.next(searchName);
  }

  private onAdvancedSearchHistorySearchNameFilterChangeDebounced(searchName: any) {
    const trimmedSearchName = searchName.trim();

    if (trimmedSearchName === '') {
      this.advancedSearchHistoryFilterKey = '';
      this.advancedSearchHistoryFilterValue = '';

      this.getAdvancedSearchHistories();
    } else if (trimmedSearchName.length === 1) {
      return;
    } else {
      this.advancedSearchHistoryFilterKey = 'search_name';
      this.advancedSearchHistoryFilterValue = trimmedSearchName;

      this.getAdvancedSearchHistories();
    }
  }

  onAdvancedSearchHistoryStartDateFilterChange(startDate: any) {
    this.advancedSearchHistoryStartDateFilter = startDate;

    if (
      this.advancedSearchHistoryStartDateFilter === '' &&
      this.advancedSearchHistoryEndDateFilter === ''
    ) {
      this.advancedSearchHistoryFilterKey = '';
      this.advancedSearchHistoryFilterValue = '';

      this.getAdvancedSearchHistories();
    }

    if (
      this.advancedSearchHistoryStartDateFilter &&
      this.advancedSearchHistoryEndDateFilter
    ) {
      this.advancedSearchHistoryFilterKey = 'created_on';
      this.getAdvancedSearchHistories();
    }
  }

  onAdvancedSearchHistoryEndDateFilterChange(endDate: any) {
    this.advancedSearchHistoryEndDateFilter = endDate;

    if (
      this.advancedSearchHistoryStartDateFilter === '' &&
      this.advancedSearchHistoryEndDateFilter === ''
    ) {
      this.advancedSearchHistoryFilterKey = '';
      this.advancedSearchHistoryFilterValue = '';

      this.getAdvancedSearchHistories();
    }

    if (
      this.advancedSearchHistoryStartDateFilter &&
      this.advancedSearchHistoryEndDateFilter
    ) {
      this.advancedSearchHistoryFilterKey = 'created_on';
      this.getAdvancedSearchHistories();
    }
  }

  resetAdvancedSearchHistories() {
    this.advancedSearchHistoriesCurrentPage = 1;

    this.advancedSearchHistoryFilterKey = '';
    this.advancedSearchHistoryFilterValue = '';

    this.advancedSearchHistoryIdFilter = '';
    this.advancedSearchHistorySearchNameFilter = '';
    this.advancedSearchHistoryStartDateFilter = '';
    this.advancedSearchHistoryEndDateFilter = '';
  }

  // BASIC SEARCH HISTORY

  getBasicSearchHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getBasicSearchHistories(
        this.basicSearchHistoriesCurrentPage,
        this.basicSearchHistoriesPerPage,
        this.basicSearchHistoryFilterKey,
        this.basicSearchHistoryFilterValue,
        this.basicSearchHistoryStartDateFilter,
        this.basicSearchHistoryEndDateFilter,
        this.basicSearchHistoriesShouldExport,
        this.basicSearchHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.basicSearchHistories = response?.data?.data_list || [];
          this.basicSearchHistoriesPaged = this.addAdditionalKeys([
            ...this.basicSearchHistories,
          ]);
          this.basicSearchHistoriesCount =
            response?.data?.total_items ?? response?.data?.total_count;
          this.basicSearchHistoriesHasPreviousPage =
            response?.data?.has_previous;
          this.basicSearchHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportBasicSearchHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportBasicSearchHistories(
        this.basicSearchHistoriesCurrentPage,
        this.basicSearchHistoriesPerPage,
        this.basicSearchHistoryFilterKey,
        this.basicSearchHistoryFilterValue,
        this.basicSearchHistoryStartDateFilter,
        this.basicSearchHistoryEndDateFilter,
        this.basicSearchHistoriesShouldExport,
        this.basicSearchHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.basicSearchHistoriesShouldExport = false;
          this.basicSearchHistoriesExportFormat = '';

          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportBasicSearchHistoriesInExcel() {
    this.basicSearchHistoriesShouldExport = true;
    this.basicSearchHistoriesExportFormat = 'xlsx';
    this.exportBasicSearchHistories();
  }

  exportBasicSearchHistoriesInCsv() {
    this.basicSearchHistoriesShouldExport = true;
    this.basicSearchHistoriesExportFormat = 'csv';
    this.exportBasicSearchHistories();
  }

  exportBasicSearchHistoriesInPdf() {
    this.basicSearchHistoriesShouldExport = true;
    this.basicSearchHistoriesExportFormat = 'pdf';
    this.exportBasicSearchHistories();
  }

  onBasicSearchHistoriesPageChange(page: any) {
    this.basicSearchHistoriesSelectAll.isSelected = false;
    this.basicSearchHistoriesCurrentPage = page;
    this.getBasicSearchHistories();
  }

  onBasicSearchHistoryPagedClick(event: Event, row: any) {
    event.stopPropagation();
    const checkbox = event.target as HTMLInputElement;
    this.updateBasicSearchHistoryPaged(checkbox.checked, row.id);
  }

  onBasicSearchHistoryRowClick(row: any) {
    if (!row?.basic_search_id) return;
    this.router.navigate(['/basic-search-result', row.basic_search_id]);
  }

  updateBasicSearchHistoryPaged(isChecked: any, searchId: any) {
    const rowToUpdate = this.basicSearchHistoriesPaged.find(
      (row: any) => row.id === searchId,
    );
    if (rowToUpdate) {
      rowToUpdate.isSelected = isChecked;
    }
  }

  deleteBasicSearchHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deleteBasicSearchHistories(this.selectedBasicSearchHistories)
      .subscribe({
        next: () => {
          this.basicSearchHistoriesSelectAll.isSelected = false;
          this.updateBasicSearchHistoriesPaged();
          this.updateBasicSearchHistoriesCurrentPage();
          this.getBasicSearchHistories();
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  updateBasicSearchHistoriesPaged() {
    this.basicSearchHistoriesPaged = this.basicSearchHistoriesPaged.filter(
      (row: any) =>
        !this.selectedBasicSearchHistories.includes(row.basic_search_id),
    );
  }

  updateBasicSearchHistoriesCurrentPage() {
    if (this.basicSearchHistoriesPaged.length === 0) {
      if (!this.basicSearchHistoriesHasNextPage) {
        if (this.basicSearchHistoriesHasPreviousPage) {
          this.basicSearchHistoriesCurrentPage--;
        }
      }
    }
  }

  onBasicSearchHistoriesSelectAllClick() {
    this.basicSearchHistoriesSelectAll.isSelected =
      !this.basicSearchHistoriesSelectAll.isSelected;

    this.basicSearchHistoriesSelectAll.isSelected
      ? this.selectAllBasicSearchHistoriesPaged()
      : this.deselectAllBasicSearchHistoriesPaged();
  }

  selectAllBasicSearchHistoriesPaged() {
    this.basicSearchHistoriesPaged = this.basicSearchHistoriesPaged.map(
      (row: any) => ({
        ...row,
        isSelected: true,
      }),
    );
  }

  deselectAllBasicSearchHistoriesPaged() {
    this.basicSearchHistoriesPaged = this.basicSearchHistoriesPaged.map(
      (row: any) => ({
        ...row,
        isSelected: false,
      }),
    );
  }

  onBasicSearchHistoryPhoneFilterChange(phone: any) {
    this.basicSearchHistoryPhoneSubject.next(phone);
  }

  private onBasicSearchHistoryPhoneFilterChangeDebounced(phone: any) {
    const trimmedPhone = phone.trim();

    if (trimmedPhone === '') {
      this.basicSearchHistoryFilterKey = '';
      this.basicSearchHistoryFilterValue = '';
      this.getBasicSearchHistories();
    } else if (trimmedPhone.length === 1) {
      return;
    } else {
      this.basicSearchHistoryFilterKey = 'searched_phone';
      this.basicSearchHistoryFilterValue = trimmedPhone;
      this.getBasicSearchHistories();
    }
  }

  onBasicSearchHistoryStartDateFilterChange(startDate: any) {
    this.basicSearchHistoryStartDateFilter = startDate;

    if (
      this.basicSearchHistoryStartDateFilter === '' &&
      this.basicSearchHistoryEndDateFilter === ''
    ) {
      this.basicSearchHistoryFilterKey = '';
      this.basicSearchHistoryFilterValue = '';
      this.getBasicSearchHistories();
    }

    if (
      this.basicSearchHistoryStartDateFilter &&
      this.basicSearchHistoryEndDateFilter
    ) {
      this.basicSearchHistoryFilterKey = 'created_on';
      this.getBasicSearchHistories();
    }
  }

  onBasicSearchHistoryEndDateFilterChange(endDate: any) {
    this.basicSearchHistoryEndDateFilter = endDate;

    if (
      this.basicSearchHistoryStartDateFilter === '' &&
      this.basicSearchHistoryEndDateFilter === ''
    ) {
      this.basicSearchHistoryFilterKey = '';
      this.basicSearchHistoryFilterValue = '';
      this.getBasicSearchHistories();
    }

    if (
      this.basicSearchHistoryStartDateFilter &&
      this.basicSearchHistoryEndDateFilter
    ) {
      this.basicSearchHistoryFilterKey = 'created_on';
      this.getBasicSearchHistories();
    }
  }

  resetBasicSearchHistories() {
    this.basicSearchHistoriesCurrentPage = 1;
    this.basicSearchHistoryFilterKey = '';
    this.basicSearchHistoryFilterValue = '';
    this.basicSearchHistoryPhoneFilter = '';
    this.basicSearchHistoryStartDateFilter = '';
    this.basicSearchHistoryEndDateFilter = '';
  }

  // WEB SEARCH HISTORY

  getWebSearchHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getWebSearchHistories(
        this.webSearchHistoriesCurrentPage,
        this.webSearchHistoriesPerPage,
        this.webSearchHistoryFilterKey,
        this.webSearchHistoryFilterValue,
        this.webSearchHistoryStartDateFilter,
        this.webSearchHistoryEndDateFilter,
        this.webSearchHistoriesShouldExport,
        this.webSearchHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.webSearchHistories = response?.data?.data_list || [];
          this.webSearchHistoriesPaged = this.addAdditionalKeys([
            ...this.webSearchHistories,
          ]);
          this.webSearchHistoriesCount = response?.data?.total_items;
          this.webSearchHistoriesHasPreviousPage = response?.data?.has_previous;
          this.webSearchHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportWebSearchHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .exportWebSearchHistories(
        this.webSearchHistoriesCurrentPage,
        this.webSearchHistoriesPerPage,
        this.webSearchHistoryFilterKey,
        this.webSearchHistoryFilterValue,
        this.webSearchHistoryStartDateFilter,
        this.webSearchHistoryEndDateFilter,
        this.webSearchHistoriesShouldExport,
        this.webSearchHistoriesExportFormat,
      )
      .subscribe({
        next: (response) => {
          this.webSearchHistoriesShouldExport = false;
          this.webSearchHistoriesExportFormat = '';

          this.saveFile(response?.data);
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  exportWebSearchHistoriesInExcel() {
    this.webSearchHistoriesShouldExport = true;
    this.webSearchHistoriesExportFormat = 'xlsx';
    this.exportWebSearchHistories();
  }

  exportWebSearchHistoriesInCsv() {
    this.webSearchHistoriesShouldExport = true;
    this.webSearchHistoriesExportFormat = 'csv';
    this.exportWebSearchHistories();
  }

  exportWebSearchHistoriesInPdf() {
    this.webSearchHistoriesShouldExport = true;
    this.webSearchHistoriesExportFormat = 'pdf';
    this.exportWebSearchHistories();
  }

  onWebSearchHistoriesPageChange(page: any) {
    this.webSearchHistoriesSelectAll.isSelected = false;
    this.webSearchHistoriesCurrentPage = page;
    this.getWebSearchHistories();
  }

  onWebSearchHistoryPagedClick(event: Event, row: any) {
    event.stopPropagation();
    const checkbox = event.target as HTMLInputElement;
    this.updateWebSearchHistoryPaged(checkbox.checked, row.id);
  }

  onWebSearchHistoryRowClick(row: any) {
    if (!row?.web_capture_id) return;
    this.router.navigate(['/web-capture-result', row.web_capture_id]);
  }

  updateWebSearchHistoryPaged(isChecked: any, searchId: any) {
    const rowToUpdate = this.webSearchHistoriesPaged.find(
      (row: any) => row.id === searchId,
    );
    if (rowToUpdate) {
      rowToUpdate.isSelected = isChecked;
    }
  }

  deleteWebSearchHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .deleteWebSearchHistories(this.selectedWebSearchHistories)
      .subscribe({
        next: () => {
          this.webSearchHistoriesSelectAll.isSelected = false;
          this.updateWebSearchHistoriesPaged();
          this.updateWebSearchHistoriesCurrentPage();
          this.getWebSearchHistories();
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  updateWebSearchHistoriesPaged() {
    this.webSearchHistoriesPaged = this.webSearchHistoriesPaged.filter(
      (row: any) => !this.selectedWebSearchHistories.includes(row.web_capture_id),
    );
  }

  updateWebSearchHistoriesCurrentPage() {
    if (this.webSearchHistoriesPaged.length === 0) {
      if (!this.webSearchHistoriesHasNextPage) {
        if (this.webSearchHistoriesHasPreviousPage) {
          this.webSearchHistoriesCurrentPage--;
        }
      }
    }
  }

  onWebSearchHistoriesSelectAllClick() {
    this.webSearchHistoriesSelectAll.isSelected =
      !this.webSearchHistoriesSelectAll.isSelected;

    this.webSearchHistoriesSelectAll.isSelected
      ? this.selectAllWebSearchHistoriesPaged()
      : this.deselectAllWebSearchHistoriesPaged();
  }

  selectAllWebSearchHistoriesPaged() {
    this.webSearchHistoriesPaged = this.webSearchHistoriesPaged.map(
      (row: any) => ({
        ...row,
        isSelected: true,
      }),
    );
  }

  deselectAllWebSearchHistoriesPaged() {
    this.webSearchHistoriesPaged = this.webSearchHistoriesPaged.map(
      (row: any) => ({
        ...row,
        isSelected: false,
      }),
    );
  }

  onWebSearchHistoryUrlFilterChange(url: any) {
    this.webSearchHistoryUrlSubject.next(url);
  }

  private onWebSearchHistoryUrlFilterChangeDebounced(url: any) {
    const trimmedUrl = url.trim();

    if (trimmedUrl === '') {
      this.webSearchHistoryFilterKey = '';
      this.webSearchHistoryFilterValue = '';
      this.getWebSearchHistories();
    } else if (trimmedUrl.length === 1) {
      return;
    } else {
      this.webSearchHistoryFilterKey = 'url';
      this.webSearchHistoryFilterValue = trimmedUrl;
      this.getWebSearchHistories();
    }
  }

  onWebSearchHistoryDomainFilterChange(domain: any) {
    this.webSearchHistoryDomainSubject.next(domain);
  }

  private onWebSearchHistoryDomainFilterChangeDebounced(domain: any) {
    const trimmedDomain = domain.trim();

    if (trimmedDomain === '') {
      this.webSearchHistoryFilterKey = '';
      this.webSearchHistoryFilterValue = '';
      this.getWebSearchHistories();
    } else if (trimmedDomain.length === 1) {
      return;
    } else {
      this.webSearchHistoryFilterKey = 'domain';
      this.webSearchHistoryFilterValue = trimmedDomain;
      this.getWebSearchHistories();
    }
  }

  onWebSearchHistoryStartDateFilterChange(startDate: any) {
    this.webSearchHistoryStartDateFilter = startDate;

    if (
      this.webSearchHistoryStartDateFilter === '' &&
      this.webSearchHistoryEndDateFilter === ''
    ) {
      this.webSearchHistoryFilterKey = '';
      this.webSearchHistoryFilterValue = '';
      this.getWebSearchHistories();
    }

    if (
      this.webSearchHistoryStartDateFilter &&
      this.webSearchHistoryEndDateFilter
    ) {
      this.webSearchHistoryFilterKey = 'created_on';
      this.getWebSearchHistories();
    }
  }

  onWebSearchHistoryEndDateFilterChange(endDate: any) {
    this.webSearchHistoryEndDateFilter = endDate;

    if (
      this.webSearchHistoryStartDateFilter === '' &&
      this.webSearchHistoryEndDateFilter === ''
    ) {
      this.webSearchHistoryFilterKey = '';
      this.webSearchHistoryFilterValue = '';
      this.getWebSearchHistories();
    }

    if (
      this.webSearchHistoryStartDateFilter &&
      this.webSearchHistoryEndDateFilter
    ) {
      this.webSearchHistoryFilterKey = 'created_on';
      this.getWebSearchHistories();
    }
  }

  resetWebSearchHistories() {
    this.webSearchHistoriesCurrentPage = 1;
    this.webSearchHistoryFilterKey = '';
    this.webSearchHistoryFilterValue = '';
    this.webSearchHistoryUrlFilter = '';
    this.webSearchHistoryDomainFilter = '';
    this.webSearchHistoryStartDateFilter = '';
    this.webSearchHistoryEndDateFilter = '';
  }

  // LINK ANALYSIS HISTORIES

  linkAnalysisHistories: any = [];
  linkAnalysisHistoriesPaged: any = [];

  linkAnalysisHistoriesCount: any;
  linkAnalysisHistoriesPerPage = 10;
  linkAnalysisHistoriesCurrentPage = 1;
  linkAnalysisHistoriesHasPreviousPage: any;
  linkAnalysisHistoriesHasNextPage: any;

  linkAnalysisHistoryFilters: any = [
    { label: 'Root Value', value: 'root-value' },
    { label: 'Root Type', value: 'root-type' },
    { label: 'Status', value: 'status' },
    { label: 'Date Added', value: 'date-added' },
  ];

  linkAnalysisHistoryFilterKey: any = '';
  linkAnalysisHistoryFilterValue: any = '';

  linkAnalysisRootValueFilter: any = '';
  linkAnalysisRootTypeFilter: any = '';
  linkAnalysisStatusFilter: any = '';
  linkAnalysisHistoryStartDateFilter: any = '';
  linkAnalysisHistoryEndDateFilter: any = '';

  linkAnalysisHistoryTableThs: any = [
    'Id',
    'Root',
    'Mode',
    'Status',
    'Nodes',
    'Edges',
    'Date Added',
    'Time',
  ];

  linkAnalysisHistoryTableTds: any = [
    { field: 'id' },
    { field: 'root_display' },
    { field: 'mode_display' },
    { field: 'status_display' },
    { field: 'node_count' },
    { field: 'edge_count' },
    { field: 'created_on', type: 'date' },
    { field: 'created_on', type: 'time' },
  ];

  get linkAnalysisHistoriesPagedStartAndEnd() {
    if (!this.linkAnalysisHistories?.length || !this.linkAnalysisHistoriesCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.linkAnalysisHistoriesCurrentPage - 1) *
      this.linkAnalysisHistoriesPerPage +
      1;
    const end = Math.min(
      start + this.linkAnalysisHistories.length - 1,
      this.linkAnalysisHistoriesCount,
    );

    return { start, end };
  }

  getLinkAnalysisHistories() {
    this.isLoading = true;
    this.searchHistoryService
      .getLinkAnalysisHistories(
        this.linkAnalysisHistoriesCurrentPage,
        this.linkAnalysisHistoriesPerPage,
        this.linkAnalysisHistoryFilterKey,
        this.linkAnalysisHistoryFilterValue,
        this.linkAnalysisHistoryStartDateFilter,
        this.linkAnalysisHistoryEndDateFilter,
      )
      .subscribe({
        next: (response) => {
          this.linkAnalysisHistories = response?.data?.data_list || [];
          this.linkAnalysisHistoriesPaged = this.addAdditionalKeys(
            this.linkAnalysisHistories.map((history: any) =>
              this.decorateLinkAnalysisHistory(history),
            ),
          );
          this.linkAnalysisHistoriesCount = response?.data?.total_items || 0;
          this.linkAnalysisHistoriesHasPreviousPage =
            response?.data?.has_previous;
          this.linkAnalysisHistoriesHasNextPage = response?.data?.has_next;
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  decorateLinkAnalysisHistory(history: any) {
    const graphPayload = history?.graph_payload || {};
    const summary = history?.summary || {};
    const rootValue =
      history?.root_value ||
      graphPayload?.rootNode?.label ||
      graphPayload?.rootNode?.value ||
      'Drill-down workspace';
    const rootType = history?.root_type
      ? this.titleCase(history.root_type)
      : history?.mode === 'drill_down'
        ? 'Drill-down'
        : 'Workspace';
    const nodeCount =
      summary?.nodeCount ??
      summary?.node_count ??
      (Array.isArray(graphPayload?.nodes) ? graphPayload.nodes.length : 0);
    const edgeCount =
      summary?.edgeCount ??
      summary?.edge_count ??
      (Array.isArray(graphPayload?.edges) ? graphPayload.edges.length : 0);

    return {
      ...history,
      root_display: `${rootType}: ${rootValue}`,
      mode_display:
        history?.mode === 'drill_down' ? 'Drill-down' : 'Automated',
      status_display: this.titleCase(history?.status || 'pending'),
      node_count: nodeCount,
      edge_count: edgeCount,
    };
  }

  titleCase(value: any): string {
    return String(value || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  onLinkAnalysisHistoryRowClick(linkAnalysisHistoryPaged: any) {
    const linkAnalysisId = linkAnalysisHistoryPaged?.link_analysis_id;
    if (!linkAnalysisId) return;
    this.router.navigate(['/link-analysis/result', linkAnalysisId]);
  }

  onLinkAnalysisHistoriesPageChange(page: any) {
    this.linkAnalysisHistoriesCurrentPage = page;
    this.getLinkAnalysisHistories();
  }

  onLinkAnalysisRootValueFilterChange(rootValue: any) {
    this.linkAnalysisRootValueSubject.next(rootValue);
  }

  private onLinkAnalysisRootValueFilterChangeDebounced(rootValue: any) {
    const trimmedRootValue = rootValue.trim();

    if (trimmedRootValue === '') {
      this.linkAnalysisHistoryFilterKey = '';
      this.linkAnalysisHistoryFilterValue = '';
    } else if (trimmedRootValue.length === 1) {
      return;
    } else {
      this.linkAnalysisHistoryFilterKey = 'root_value';
      this.linkAnalysisHistoryFilterValue = trimmedRootValue;
    }

    this.linkAnalysisHistoriesCurrentPage = 1;
    this.getLinkAnalysisHistories();
  }

  onLinkAnalysisRootTypeFilterChange(rootType: any) {
    this.linkAnalysisRootTypeSubject.next(rootType);
  }

  private onLinkAnalysisRootTypeFilterChangeDebounced(rootType: any) {
    const trimmedRootType = rootType.trim().toLowerCase();

    if (trimmedRootType === '') {
      this.linkAnalysisHistoryFilterKey = '';
      this.linkAnalysisHistoryFilterValue = '';
    } else if (trimmedRootType.length === 1) {
      return;
    } else {
      this.linkAnalysisHistoryFilterKey = 'root_type';
      this.linkAnalysisHistoryFilterValue = trimmedRootType;
    }

    this.linkAnalysisHistoriesCurrentPage = 1;
    this.getLinkAnalysisHistories();
  }

  onLinkAnalysisStatusFilterChange(status: any) {
    this.linkAnalysisStatusSubject.next(status);
  }

  private onLinkAnalysisStatusFilterChangeDebounced(status: any) {
    const trimmedStatus = status.trim().toLowerCase();

    if (trimmedStatus === '') {
      this.linkAnalysisHistoryFilterKey = '';
      this.linkAnalysisHistoryFilterValue = '';
    } else if (trimmedStatus.length === 1) {
      return;
    } else {
      this.linkAnalysisHistoryFilterKey = 'status';
      this.linkAnalysisHistoryFilterValue = trimmedStatus;
    }

    this.linkAnalysisHistoriesCurrentPage = 1;
    this.getLinkAnalysisHistories();
  }

  onLinkAnalysisHistoryStartDateFilterChange(startDate: any) {
    this.linkAnalysisHistoryStartDateFilter = startDate;

    if (
      this.linkAnalysisHistoryStartDateFilter === '' &&
      this.linkAnalysisHistoryEndDateFilter === ''
    ) {
      this.linkAnalysisHistoryFilterKey = '';
      this.linkAnalysisHistoryFilterValue = '';
      this.getLinkAnalysisHistories();
    }

    if (
      this.linkAnalysisHistoryStartDateFilter &&
      this.linkAnalysisHistoryEndDateFilter
    ) {
      this.linkAnalysisHistoryFilterKey = 'created_on';
      this.linkAnalysisHistoriesCurrentPage = 1;
      this.getLinkAnalysisHistories();
    }
  }

  onLinkAnalysisHistoryEndDateFilterChange(endDate: any) {
    this.linkAnalysisHistoryEndDateFilter = endDate;

    if (
      this.linkAnalysisHistoryStartDateFilter === '' &&
      this.linkAnalysisHistoryEndDateFilter === ''
    ) {
      this.linkAnalysisHistoryFilterKey = '';
      this.linkAnalysisHistoryFilterValue = '';
      this.getLinkAnalysisHistories();
    }

    if (
      this.linkAnalysisHistoryStartDateFilter &&
      this.linkAnalysisHistoryEndDateFilter
    ) {
      this.linkAnalysisHistoryFilterKey = 'created_on';
      this.linkAnalysisHistoriesCurrentPage = 1;
      this.getLinkAnalysisHistories();
    }
  }

  resetLinkAnalysisHistories() {
    this.linkAnalysisHistoriesCurrentPage = 1;

    this.linkAnalysisHistoryFilterKey = '';
    this.linkAnalysisHistoryFilterValue = '';

    this.linkAnalysisRootValueFilter = '';
    this.linkAnalysisRootTypeFilter = '';
    this.linkAnalysisStatusFilter = '';
    this.linkAnalysisHistoryStartDateFilter = '';
    this.linkAnalysisHistoryEndDateFilter = '';
  }
}
